<template>
  <div v-if="parentSelectionModal" class="parent-selection-overlay" @click.self="cancelParentSelection">
    <section class="parent-selection-dialog" role="dialog" aria-modal="true" aria-labelledby="parent-selection-title">
      <h2 id="parent-selection-title">انتخاب حساب والد</h2>
      <p>برای این شماره چند حساب پیدا شد. لطفاً کد موردنظر را انتخاب کنید.</p>
      <div class="parent-selection-list">
        <label
          v-for="parent in parentMatches"
          :key="parent.id_subcode"
          class="parent-selection-option"
          :class="{ 'is-selected': String(selectedParentSubcode) === String(parent.id_subcode) }"
        >
          <input v-model="selectedParentSubcode" type="radio" :value="String(parent.id_subcode)">
          <span class="parent-selection-option-copy">
            <strong>{{ parent.title || 'بدون نام' }}</strong>
            <span>کد: {{ parent.id_subcode }}</span>
            <span v-if="parent.address && parent.address !== '-'">{{ parent.address }}</span>
          </span>
        </label>
      </div>
      <p v-if="parentSelectionError" class="parent-selection-error" role="alert">{{ parentSelectionError }}</p>
      <div class="parent-selection-actions">
        <button type="button" class="parent-selection-cancel" @click="cancelParentSelection">انصراف</button>
        <button type="button" class="parent-selection-confirm" :disabled="!selectedParentSubcode || loading" @click="continueWithSelectedParent">
          {{ loading ? 'در حال بررسی…' : 'ادامه' }}
        </button>
      </div>
    </section>
  </div>

  <RegisterationModal
    v-model="showCustomerRegistrationModal"
    :profile="customerRegistrationProfile"
    :required="true"
    @completed="onCustomerRegistrationCompleted"
  />

  <div class="body messenger-page" :dir="currentLanguage === 'en' ? 'ltr' : 'rtl'">
    <header class="app-header select-company-header">
      <nav class="header-content select-company-nav" :aria-label="$t('brand')">
        <div class="select-company-brand">
          <a class="nav-brand" href="#" :aria-label="$t('brand')" @click.prevent>
            <img :src="navbarLogo" :alt="$t('brand')" />
          </a>
          <p class="almas-text">{{ $t('brand') }}</p>
        </div>
        <div class="header-actions">
          <div class="header-utility-controls">
            <button type="button" class="btn-logout" :title="$t('logout')" :aria-label="$t('logout')" @click="logout">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M10 17l5-5-5-5"></path>
                <path d="M15 12H3"></path>
                <path d="M12 3h7v18h-7"></path>
              </svg>
            </button>
            <div class="company-language-picker" @keydown.esc.stop="languageMenuOpen = false">
              <button type="button" class="company-language-trigger" :aria-expanded="languageMenuOpen" aria-haspopup="listbox" :title="activeLanguageOption.label" :aria-label="labels.language" @click.stop="languageMenuOpen = !languageMenuOpen">
                <span class="company-language-code">{{ activeLanguageOption.value.toUpperCase() }}</span>
                <svg class="company-language-chevron" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="m5 7.5 5 5 5-5"></path>
                </svg>
              </button>
              <div v-if="languageMenuOpen" class="company-language-menu" role="listbox" :aria-label="labels.language">
                <button v-for="language in languageOptions" :key="language.value" type="button" class="company-language-option" role="option" :aria-selected="currentLanguage === language.value" @click="changeLanguage(language.value); languageMenuOpen = false">
                  <span aria-hidden="true">{{ language.flag }}</span>
                  <span>{{ language.label }}</span>
                </button>
              </div>
            </div>
            <button type="button" class="theme-toggle" :title="currentTheme === 'dark' ? labels.light : labels.dark" :aria-label="currentTheme === 'dark' ? labels.light : labels.dark" :aria-pressed="currentTheme === 'dark'" @click="toggleTheme">
              <span class="theme-glyph" aria-hidden="true">{{ currentTheme === 'dark' ? '☼' : '☾' }}</span>
            </button>
          </div>
        </div>
      </nav>
    </header>



    <section v-if="activeDirectory !== 'profile' && activeTab !== 'ai' && directoryTypeFilter !== 'ai'" class="company-showcase" :aria-label="showcaseLabels.title">
      <section class="showcase-group showcase-about-group" :aria-label="showcaseLabels.aboutTitle">
        <div class="showcase-section-heading">
          <div class="showcase-carousel-controls" :class="{ 'has-overflow': showcaseCarouselEdges.about.left || showcaseCarouselEdges.about.right }" role="group" :aria-label="showcaseLabels.carouselControls" @pointerenter="pauseShowcaseAutoplay" @pointerleave="resumeShowcaseAutoplay" @focusin="pauseShowcaseAutoplay" @focusout="resumeShowcaseAutoplay">
            <button class="showcase-carousel-nav showcase-carousel-nav--previous" type="button" :aria-label="showcaseLabels.previous" :title="showcaseLabels.previous" @click="moveShowcaseCarousel('about', -1)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"></path></svg>
            </button>
            <button class="showcase-carousel-nav showcase-carousel-nav--next" type="button" :aria-label="showcaseLabels.next" :title="showcaseLabels.next" @click="moveShowcaseCarousel('about', 1)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"></path></svg>
            </button>
          </div>
        </div>
        <div class="showcase-carousel-shell" :class="showcaseCarouselEdgeClasses('about')">
        <div class="showcase-quick-links showcase-carousel-track"
             ref="aboutCarousel"
             @scroll.passive="syncShowcaseCarousel('about', $event)"
             @pointerenter="pauseShowcaseAutoplay"
             @pointerleave="resumeShowcaseAutoplay"
             @focusin="pauseShowcaseAutoplay"
             @focusout="resumeShowcaseAutoplay"
             @touchstart.passive="pauseShowcaseAutoplay"
             @touchend="resumeShowcaseAutoplay"
             @touchcancel="resumeShowcaseAutoplay">
        <a 
          class="showcase-quick-card" 
          href="javascript:void(0)" 
          @click.prevent="handleContactUsClick"
        >
          <svg class="showcase-quick-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <span class="showcase-quick-copy">
            <strong>{{ isConnectingSupport ? 'در حال اتصال...' : 'ارتباط با ما' }}</strong>
          </span>
        </a>

        <a class="showcase-quick-card" href="/landing#about">
          <span class="showcase-quick-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"></circle><path d="M12 11v5M12 8h.01"></path></svg>
          </span>
          <span class="showcase-quick-copy"><strong>درباره ما</strong></span>
        </a>
        <a class="showcase-quick-card" href="/landing#modules">
          <span class="showcase-quick-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="8" height="8" rx="2"></rect><rect x="13" y="3" width="8" height="5" rx="2"></rect><rect x="13" y="10" width="8" height="11" rx="2"></rect><rect x="3" y="13" width="8" height="8" rx="2"></rect></svg>
          </span>
          <span class="showcase-quick-copy"><strong>راهنما</strong></span>
        </a>
      </div>
        </div>
      </section>

      <section v-if="featuredCompanies.length!=0" class="showcase-group" aria-labelledby="featured-companies-title">
        <div class="showcase-section-heading">
          <div>
            <h2 id="featured-companies-title">شرکت های برگزیده</h2>
          </div>
          <div class="showcase-carousel-controls" :class="{ 'has-overflow': showcaseCarouselEdges.companies.left || showcaseCarouselEdges.companies.right }" role="group" :aria-label="showcaseLabels.carouselControls" @pointerenter="pauseShowcaseAutoplay" @pointerleave="resumeShowcaseAutoplay" @focusin="pauseShowcaseAutoplay" @focusout="resumeShowcaseAutoplay">
            <button class="showcase-carousel-nav showcase-carousel-nav--previous" type="button" :aria-label="showcaseLabels.previous" :title="showcaseLabels.previous" @click="moveShowcaseCarousel('companies', -1)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"></path></svg>
            </button>
            <button class="showcase-carousel-nav showcase-carousel-nav--next" type="button" :aria-label="showcaseLabels.next" :title="showcaseLabels.next" @click="moveShowcaseCarousel('companies', 1)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"></path></svg>
            </button>
          </div>
        </div>
        <div class="showcase-carousel-shell" :class="showcaseCarouselEdgeClasses('companies')">
        <div class="showcase-card-grid showcase-carousel-track"
             ref="companyCarousel"
             @scroll.passive="syncShowcaseCarousel('companies', $event)"
             @pointerenter="pauseShowcaseAutoplay"
             @pointerleave="resumeShowcaseAutoplay"
             @focusin="pauseShowcaseAutoplay"
             @focusout="resumeShowcaseAutoplay"
             @touchstart.passive="pauseShowcaseAutoplay"
             @touchend="resumeShowcaseAutoplay"
             @touchcancel="resumeShowcaseAutoplay">
          <div v-for="company in featuredCompanies" @click="handleFeaturedClick(company)" :key="'featured-company-' + company.company_id" class="showcase-company-card">
            <div v-if="loadingFeaturedId === company.company_id" class="card-loading-overlay">
              <div class="spinner"></div>
            </div>
            <div class="showcase-company-card-top">
              <span class="showcase-company-avatar">
                <img v-if="company.company_logo" :src="getProxiedImage(company.company_logo)" alt="" />
                <span v-else>{{ company.avatar_text || company.company_name?.trim()?.charAt(0) }}</span>
              </span>
            </div>
            <h3>{{ company.company_name }}</h3>
            <!-- <p>{{ company.description || company.activity_type || showcaseLabels.softwareDescription }}</p> -->
          </div>
        </div>
        </div>
      </section>

      <section v-if="featuredStores.length!=0" class="showcase-group" aria-labelledby="featured-stores-title">
        <div class="showcase-section-heading">
          <h2 id="featured-stores-title">فروشگاه های برگزیده</h2>
          <div class="showcase-carousel-controls" :class="{ 'has-overflow': showcaseCarouselEdges.stores.left || showcaseCarouselEdges.stores.right }" role="group" :aria-label="showcaseLabels.carouselControls" @pointerenter="pauseShowcaseAutoplay" @pointerleave="resumeShowcaseAutoplay" @focusin="pauseShowcaseAutoplay" @focusout="resumeShowcaseAutoplay">
            <button class="showcase-carousel-nav showcase-carousel-nav--previous" type="button" :aria-label="showcaseLabels.previous" :title="showcaseLabels.previous" @click="moveShowcaseCarousel('stores', -1)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"></path></svg>
            </button>
            <button class="showcase-carousel-nav showcase-carousel-nav--next" type="button" :aria-label="showcaseLabels.next" :title="showcaseLabels.next" @click="moveShowcaseCarousel('stores', 1)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"></path></svg>
            </button>
          </div>
        </div>
        <div class="showcase-carousel-shell" :class="showcaseCarouselEdgeClasses('stores')">
        <div class="showcase-card-grid showcase-carousel-track"
             ref="storeCarousel"
             @scroll.passive="syncShowcaseCarousel('stores', $event)"
             @pointerenter="pauseShowcaseAutoplay"
             @pointerleave="resumeShowcaseAutoplay"
             @focusin="pauseShowcaseAutoplay"
             @focusout="resumeShowcaseAutoplay"
             @touchstart.passive="pauseShowcaseAutoplay"
             @touchend="resumeShowcaseAutoplay"
             @touchcancel="resumeShowcaseAutoplay">
          <div v-for="company in featuredStores" @click="handleFeaturedClick(company)" :key="'featured-store-' + company.company_id" class="showcase-company-card">
            <div v-if="loadingFeaturedId === company.company_id" class="card-loading-overlay">
              <div class="spinner"></div>
            </div>
            <div class="showcase-company-card-top">
              <span class="showcase-company-avatar">
                <img v-if="company.company_logo" :src="getProxiedImage(company.company_logo)" alt="" />
                <span v-else>{{ company.avatar_text || company.company_name?.charAt(0) }}</span>
              </span>
            </div>
            <h3>{{ company.company_name }}</h3>
            <!-- <p>{{ company.description || company.activity_type || showcaseLabels.storeDescription }}</p> -->
          </div>
        </div>
        </div>
      </section>
    </section>
    <section v-if="activeDirectory !== 'profile'" class="desktop-page-heading" :aria-label="activeTab === 'ai' ? labels.ai : labels.companies">
      <div class="desktop-heading-actions">
        <div class="desktop-heading-copy">
          <h1>شرکتها</h1>
          <span class="desktop-heading-count">{{ activeTab === 'ai' ? filteredAiCompanies.length : filteredCompanies.length }}</span>
        </div>
        <button v-if="activeTab !== 'ai'" type="button" class="desktop-add-company-button" :title="$t('company.create.title')" @click="openCreateCompanyModal">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"></path></svg>
          <!-- <span>{{ $t('company.create.title') }}</span> -->
        </button>
      </div>
    </section>

    <section v-if="activeDirectory !== 'profile' && activeTab !== 'ai'" class="directory-controls"  :aria-label="labels.search + ' ' + labels.filters">
      <label class="directory-search">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7"></circle>
          <path d="m20 20-4-4"></path>
        </svg>
        <input v-model="searchQuery" type="search" placeholder="جست‌وجو در شرکت‌ها، فروشگاه‌ها و هوش مصنوعی" aria-label="جست‌وجو در همه موارد" />
      </label>
      <button class="filter-toggle" type="button" :aria-expanded="showFilterPanel" @click="showFilterPanel = !showFilterPanel">
        <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
          <path d="M4 6h16M7 12h10m-7 6h4"></path>
        </svg>
        <span>فیلترها</span>
        <span v-if="activeFilterCount" class="filter-count">{{ activeFilterCount }}</span>
      </button>
    </section>

    <section v-if="activeDirectory !== 'profile' && activeTab === 'ai'" class="directory-controls ai-directory-controls" :aria-label="labels.aiSearch">
      <label class="directory-search">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7"></circle>
          <path d="m20 20-4-4"></path>
        </svg>
        <input v-model="searchQuery" type="search" placeholder="جست‌وجو در همه موارد" aria-label="جست‌وجو در همه موارد" />
      </label>
    </section>
    <section v-if="activeDirectory !== 'profile' && showFilterPanel && activeTab !== 'ai'" class="company-filter-panel" :aria-label="labels.filters">
      <div class="filter-panel-heading">
        <h2>فیلترها</h2>
        <button type="button" class="filter-close" aria-label="بستن فیلترها" @click="showFilterPanel = false">×</button>
      </div>
      <label class="filter-field">
        <span>نمایش بر اساس</span>
        <select v-model="directoryTypeFilter">
          <option value="all">همه موارد</option>
          <option value="companies">شرکت‌ها</option>
          <option value="stores">فروشگاه‌ها</option>
          <option value="ai">هوش مصنوعی</option>
        </select>
      </label>
      <label class="filter-field">
        <span>نوع شرکت</span>
        <select v-model="companyFilterType">
          <option value="all">همه انواع</option>
          <option value="private">خصوصی</option>
          <option value="public">عمومی</option>
        </select>
      </label>
      <button type="button" class="clear-filters" @click="clearFilters">پاک کردن فیلترها</button>
    </section>
    <!-- Create Company Modal -->
    <div v-if="showCreateModal" class="modal-overlay" @click="closeCreateCompanyModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="text-colored">{{ $t('company.create.title') }}</h3>
          <button class="modal-close" @click="closeCreateCompanyModal">×</button>
        </div>

        <form @submit.prevent="openAgreementModal" class="create-company-form" enctype="multipart/form-data">

          <!-- Required Fields Notice -->
          <div class="required-notice">
            <span class="required-star">*</span>
            {{ $t('company.messages.required_fields') }}
          </div>

          <!-- Basic Information -->
          <div class="form-section">
            <h4>{{ $t('company.fields.basic_info') }}</h4>

            <div class="form-group required" id="field-company_name" ref="errorRefs">
              <label>{{ $t('company.fields.company_name') }} <span class="required-star">*</span></label>
              <input 
                v-model="formData.company_name" 
                type="text" 
                required
                :placeholder="$t('company.fields.company_name_placeholder')"
                :class="{ 'error': validationErrors.company_name }"
              />
              <span v-if="validationErrors.company_name" class="error-message">
                {{ validationErrors.company_name }}
              </span>
            </div>

            <div class="form-row">
              <div class="form-group half required" id="field-national_id" ref="errorRefs">
                <label>{{ $t('company.fields.national_id') }} <!--<span class="required-star">*</span>--></label>
                <input 
                  v-model="formData.national_id" 
                  type="text" 
                  required
                  :placeholder="$t('company.fields.national_id_placeholder')"
                  :class="{ 'error': validationErrors.national_id }"
                />
                <span v-if="validationErrors.national_id" class="error-message">
                  {{ validationErrors.national_id }}
                </span>
              </div>

              <div class="form-group half required" id="field-economic_id" ref="errorRefs">
                <label>{{ $t('company.fields.economic_id') }} <!--<span class="required-star">*</span>--></label>
                <input 
                  v-model="formData.economic_id" 
                  type="text" 
                  required
                  :placeholder="$t('company.fields.economic_id_placeholder')"
                  :class="{ 'error': validationErrors.economic_id }"
                />
                <span v-if="validationErrors.economic_id" class="error-message">
                  {{ validationErrors.economic_id }}
                </span>
              </div>
            </div>

            <div class="form-group required" id="field-activity_type" ref="errorRefs">
              <label>{{ $t('company.fields.activity_type') }} <span class="required-star">*</span></label>
              <input 
                v-model="formData.activity_type" 
                type="text" 
                :placeholder="$t('company.fields.activity_type_placeholder')"
                :class="{ 'error': validationErrors.activity_type }"
              />
              <span v-if="validationErrors.activity_type" class="error-message">
                {{ validationErrors.activity_type }}
              </span>
            </div>

            <div class="form-group required" id="field-country" ref="errorRefs">
              <label>{{ $t('company.fields.country') }} <span class="required-star">*</span></label>
              <input 
                v-model="formData.country" 
                type="text" 
                :placeholder="$t('company.fields.country_placeholder')"
                :class="{ 'error': validationErrors.country }"
              />
              <span v-if="validationErrors.country" class="error-message">
                {{ validationErrors.country }}
              </span>
            </div>
          </div>

          <!-- Logo Upload -->
          <div class="form-section">
            <h4>{{ $t('company.fields.logo') }}</h4>

            <div class="file-upload-container">
              <div class="file-upload-preview" v-if="logoPreview">
                <img :src="logoPreview" alt="Logo preview" />
                <button type="button" class="remove-file" @click="removeLogo">×</button>
              </div>
              <div v-else class="file-upload-placeholder" @click="triggerLogoUpload">
                <svg width="40" height="40" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21 15 16 10 5 21"></polyline>
                </svg>
                <span>{{ $t('company.fields.logo_placeholder') }}</span>
              </div>

              <input 
                ref="logoInput"
                type="file"
                accept="image/*"
                @change="handleLogoUpload"
                style="display:none"
              />
            </div>
          </div>

          <!-- Contact Information -->
          <div class="form-section">
            <h4>{{ $t('company.fields.contact_info') }}</h4>

            <div class="form-group">
              <label>{{ $t('company.fields.website') }}</label>
              <input 
                v-model="formData.company_website_url" 
                type="text" 
                :placeholder="$t('company.fields.website_placeholder')"
              />
            </div>

            <div class="form-group required" id="field-company_address" ref="errorRefs">
              <label>{{ $t('company.fields.address') }} <span class="required-star">*</span></label>
              <textarea 
                v-model="formData.company_address" 
                rows="2"
                :placeholder="$t('company.fields.address_placeholder')"
                :class="{ 'error': validationErrors.company_address }"
              ></textarea>
              <span v-if="validationErrors.company_address" class="error-message">
                {{ validationErrors.company_address }}
              </span>
            </div>

            <div class="form-group half required" id="field-post_code" ref="errorRefs">
              <label>{{ $t('company.fields.postal_code') }}</label>
              <input 
                v-model="formData.post_code" 
                type="text" 
                :placeholder="$t('company.fields.postal_code_placeholder')"
                :class="{ 'error': validationErrors.post_code }"
              />
              <span v-if="validationErrors.post_code" class="error-message">
                {{ validationErrors.post_code }}
              </span>
            </div>

            <!-- Location -->
            <div class="form-group">
              <label>{{ $t('company.fields.location') }}</label>

              <!-- Map Container -->
              <div class="location-map-container">
                <div id="location-picker-map" ref="locationMapElement" class="location-map"></div>
              </div>

              <div class="location-coordinates" v-if="formData.location_lat && formData.location_lng">
                <span>{{ $t('company.fields.lat') }}: {{ formData.location_lat.toFixed(6) }}</span>
                <span>{{ $t('company.fields.lng') }}: {{ formData.location_lng.toFixed(6) }}</span>
                <button type="button" class="btn-small" @click="clearLocation">
                  {{ $t('company.actions.clear') }}
                </button>
              </div>

              <div class="location-search">
                <input 
                  type="text"
                  v-model="locationSearch"
                  :placeholder="$t('company.fields.location_search')"
                  @keyup.enter="searchLocation"
                />
                <button type="button" class="btn-small" @click="searchLocation">
                  {{ $t('company.actions.search') }}
                </button>
              </div>
            </div>
          </div>

          <!-- Descriptions -->
          <div class="form-section">
            <h4>{{ $t('company.fields.descriptions') }}</h4>

            <div class="form-group required" id="field-description" ref="errorRefs">
              <label>{{ $t('company.fields.short_description') }} <span class="required-star">*</span></label>
              <textarea 
                v-model="formData.description" 
                rows="2"
                :placeholder="$t('company.fields.short_description_placeholder')"
                :class="{ 'error': validationErrors.description }"
              ></textarea>
              <span v-if="validationErrors.description" class="error-message">
                {{ validationErrors.description }}
              </span>
            </div>

            <div class="form-group">
              <label>{{ $t('company.fields.full_description') }}</label>
              <textarea 
                v-model="formData.complete_description" 
                rows="3"
                :placeholder="$t('company.fields.full_description_placeholder')"
              ></textarea>
            </div>
          </div>

          <!-- Services -->
          <div class="form-section">
            <h4>{{ $t('company.services.title') }}</h4>

            <div 
              v-for="(service, index) in formData.services" 
              :key="index"
              class="service-item"
            >
              <div class="service-header">
                <span>{{ $t('company.services.service') }} {{ index + 1 }}</span>
                <button 
                  v-if="formData.services.length > 1" 
                  type="button"
                  class="remove-service-btn"
                  @click="removeService(index)"
                >
                  ×
                </button>
              </div>

              <div class="form-group">
                <label>{{ $t('company.services.title_label') }}</label>
                <input 
                  v-model="service.title"
                  type="text"
                  :placeholder="$t('company.services.title_placeholder', { num: index+1 })"
                />
              </div>

              <div class="form-group">
                <label>{{ $t('company.services.desc_label') }}</label>
                <textarea 
                  v-model="service.description"
                  rows="2"
                  :placeholder="$t('company.services.desc_placeholder', { num: index+1 })"
                ></textarea>
              </div>

              <div class="file-upload-container small">
                <div class="file-upload-preview" v-if="serviceImagesPreview[index]">
                  <img :src="serviceImagesPreview[index]" />
                  <button type="button" class="remove-file" @click="removeServiceImage(index)">×</button>
                </div>
                <div v-else class="file-upload-placeholder" @click="triggerServiceImageUpload(index)">
                  <svg width="30" height="30" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                  <span>{{ $t('company.services.image') }}</span>
                </div>
                <input 
                  :ref="el => { if (el) serviceImageInputs[index] = el }"
                  type="file" 
                  accept="image/*"
                  @change="(e) => handleServiceImageUpload(e, index)"
                  style="display:none"
                />
              </div>
            </div>

            <button 
              v-if="formData.services.length < 4" 
              type="button"
              class="add-service-btn"
              @click="addService"
            >
              + {{ $t('company.services.add') }}
            </button>
          </div>

          <!-- Settings -->
          <div class="form-section">
            <h4>{{ $t('company.fields.settings') }}</h4>

            <div class="form-row">
              <div class="form-group half required" id="field-company_category" ref="errorRefs">
                <label>{{ $t('company.fields.company_category') }} <span class="required-star">*</span></label>
                <select 
                  v-model="formData.company_category"
                  :class="{ 'error': validationErrors.company_category }"
                >
                  <option value="private">{{ $t('company.fields.category_private') }}</option>
                  <option value="public">{{ $t('company.fields.category_public_company') }}</option>
                  <option value="none">{{ $t('company.fields.no_category') }}</option>
                </select>
                <span v-if="validationErrors.company_category" class="error-message">
                  {{ validationErrors.company_category }}
                </span>
              </div>

              <div class="form-group half required" id="field-store_category" ref="errorRefs">
                <label>{{ $t('company.fields.store_category') }} <span class="required-star">*</span></label>
                <select 
                  v-model="formData.store_category"
                  :class="{ 'error': validationErrors.store_category }"
                >
                  <option value="private">{{ $t('company.fields.category_private') }}</option>
                  <option value="public">{{ $t('company.fields.category_public_company') }}</option>
                  <option value="none">{{ $t('company.fields.no_category') }}</option>
                </select>
                <span v-if="validationErrors.store_category" class="error-message">
                  {{ validationErrors.store_category }}
                </span>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group half">
                <label>{{ $t('company.fields.currency') }}</label>
                <select v-model="formData.vahed_pool">
                  <option :value="1">{{ $t('company.fields.currency_rial') }}</option>
                  <option :value="3">{{ $t('company.fields.currency_usd') }}</option>
                </select>
              </div>

              <div class="form-group half checkbox">
                <label>
                  <input type="checkbox" v-model="formData.multi_arz" />
                  {{ $t('company.fields.multi_currency') }}
                </label>
              </div>
            </div>
          </div>

          <!-- Modal Actions -->
          <div class="modal-actions">
            <button 
              type="submit" 
              class="btn-primary"
              :disabled="creatingCompany || !isFormValid"
            >
              {{ creatingCompany ? $t('company.actions.creating') : $t('company.actions.create') }}
            </button>

            <button 
              type="button" 
              class="btn-secondary"
              @click="closeCreateCompanyModal"
            >
              {{ $t('company.actions.cancel') }}
            </button>
          </div>

        </form>
      </div>
    </div>

    <!-- Agreement Modal -->
    <div v-if="showAgreementModal" class="modal-overlay">
      <div class="modal-content agreement-modal" @click.stop>

        <div class="modal-header">
          <h3>📜 {{ $t('company.agreement.title') }}</h3>
          <button class="modal-close" @click="showAgreementModal=false">×</button>
        </div>

        <div class="agreement-content">

          <p>{{ $t('company.agreement.intro') }}</p>

          <p><b>{{ $t('company.agreement.section1_title') }}</b></p>
          <ul>
            <li>{{ $t('company.agreement.section1_item1') }}</li>
            <li>{{ $t('company.agreement.section1_item2') }}</li>
            <li>{{ $t('company.agreement.section1_item3') }}</li>
          </ul>

          <p><b>{{ $t('company.agreement.section2_title') }}</b></p>
          <ul>
            <li>{{ $t('company.agreement.section2_item1') }}</li>
            <li>{{ $t('company.agreement.section2_item2') }}</li>
          </ul>

          <p><b>{{ $t('company.agreement.section3_title') }}</b></p>
          <ul>
            <li>{{ $t('company.agreement.section3_item1') }}</li>
            <li>{{ $t('company.agreement.section3_item2') }}</li>
          </ul>

          <p><b>{{ $t('company.agreement.section4_title') }}</b></p>
          <ul>
            <li>{{ $t('company.agreement.section4_item1') }}</li>
          </ul>

          <p><b>{{ $t('company.agreement.section5_title') }}</b></p>
          <ul>
            <li>{{ $t('company.agreement.section5_item1') }}</li>
          </ul>

        </div>

        <div class="agreement-checkbox">
          <label>
            <input type="checkbox" v-model="agreementAccepted" />
            {{ $t('company.agreement.accept_terms') }}
          </label>
        </div>

        <div class="modal-actions">

          <button
            class="btn-primary"
            :disabled="!agreementAccepted || creatingCompany"
            @click="confirmCreateCompany"
          >
            {{ $t('company.agreement.confirm_button') }}
          </button>

          <button
            class="btn-secondary"
            @click="showAgreementModal=false"
          >
            {{ $t('company.agreement.cancel_button') }}
          </button>

        </div>

      </div>
    </div>

    <!-- Company Switch Confirmation Modal -->
    <div v-if="showSwitchConfirmModal" class="modal-overlay">
        <div class="modal-content confirm-modal" @click.stop>
            <div class="modal-header">
                <h3>⚠️ {{ $t('company.switch.confirm_title') || 'تغییر شرکت' }}</h3>
                <button class="modal-close" @click="closeSwitchConfirmModal">×</button>
            </div>

            <div class="confirm-content">
                <div class="confirm-icon">🛒</div>
                <p>{{ $t('company.switch.confirm_message') || 'شما آیتم‌هایی در سبد خرید دارید.' }}</p>
                <p class="confirm-detail">
                    {{ $t('company.switch.confirm_detail') || 'با تغییر شرکت، تمام آیتم‌های موجود در سبد خرید حذف خواهند شد. آیا مطمئن هستید؟' }}
                </p>
            </div>

            <div class="modal-actions">
                <button
                    class="btn-danger"
                    @click="confirmSwitchCompany"
                >
                    {{ $t('company.switch.continue') || 'ادامه و حذف آیتم‌ها' }}
                </button>
                <button
                    class="btn-secondary"
                    @click="closeSwitchConfirmModal"
                >
                    {{ $t('company.switch.cancel') || 'انصراف' }}
                </button>
            </div>
        </div>
    </div>

    <!-- Switching Note -->
    <div v-if="isSwitching" class="switching-note">
      <p>{{ $t('company.messages.switching_company') }}</p>
    </div>

    <div class="chat-list-container" :class="{ 'is-profile-view': activeDirectory === 'profile' }">
      <section v-if="activeDirectory === 'profile'" class="profile-page">
        <header class="profile-page-heading">
          <div>
            <span class="profile-page-eyebrow">تنظیمات حساب</span>
            <h1>پروفایل کاربری</h1>
            <p>اطلاعات حساب و تصویر پروفایل خود را مدیریت کنید.</p>
          </div>
          <span class="profile-page-breadcrumb">حساب کاربری <b>›</b> پروفایل</span>
        </header>

        <section class="profile-card">
          <h2>اطلاعات حساب</h2>
          <p class="profile-section-caption">تصویر و مشخصات حساب کاربری</p>

          <div class="profile-photo-row">
            <div class="profile-photo-upload" role="button" tabindex="0" :aria-disabled="profileLoading || updatingProfile || uploadingProfilePhoto" :class="{ 'is-disabled': profileLoading || updatingProfile || uploadingProfilePhoto }" @click="openProfilePhotoPicker" @keydown.enter.prevent="openProfilePhotoPicker" @keydown.space.prevent="openProfilePhotoPicker" @dragover.prevent @drop.prevent="handleProfilePhotoDrop">
              <input ref="profilePhotoInput" type="file" accept="image/*" class="profile-photo-input" @change="handleProfilePhotoChange" />
              <span class="profile-upload-icon" aria-hidden="true">＋</span>
              <strong>{{ profilePhotoFile ? 'تصویر جدید انتخاب شد' : 'برای انتخاب تصویر کلیک کنید' }}</strong>
              <span>فرمت‌های JPG، PNG یا WEBP · حداکثر ۵ مگابایت</span>
            </div>
            <div class="profile-photo-preview">
              <img v-if="profilePhotoPreview" :src="profilePhotoPreview" alt="پیش‌نمایش تصویر پروفایل" />
              <span v-else class="profile-photo-fallback">{{ profileInitial }}</span>
            </div>
          </div>

          <p v-if="profileError" class="profile-form-message is-error" role="alert">{{ profileError }}</p>
          <p v-if="profileMessage" class="profile-form-message is-success" role="status">{{ profileMessage }}</p>
          <p v-if="profileLoading" class="profile-form-message" role="status">در حال دریافت اطلاعات پروفایل…</p>

          <div class="profile-fields-grid">
            <label><span>نام</span><input v-model="profileData.first_name" type="text" autocomplete="given-name" :disabled="profileLoading || updatingProfile" /></label>
            <label><span>نام خانوادگی</span><input v-model="profileData.last_name" type="text" autocomplete="family-name" :disabled="profileLoading || updatingProfile" /></label>
            <label><span>ایمیل</span><input v-model="profileData.email" type="email" autocomplete="email" :disabled="profileLoading || updatingProfile" /></label>
            <label><span>شماره تلفن</span><input v-model="profileData.phone_number" type="tel" disabled /></label>
            <label><span>کد ملی</span><input v-model="profileData.national_code" type="text" inputmode="numeric" maxlength="10" :disabled="profileLoading || updatingProfile" /></label>
            <label><span>کد پستی</span><input v-model="profileData.postal_code" type="text" inputmode="numeric" maxlength="10" :disabled="profileLoading || updatingProfile" /></label>
            <label class="profile-address-field"><span>نشانی</span><textarea v-model="profileData.address" rows="3" autocomplete="street-address" :disabled="profileLoading || updatingProfile"></textarea></label>
            <label><span>محل کار</span><input v-model="profileData.mohal_kar" type="text" :disabled="profileLoading || updatingProfile" /></label>
          </div>

          <footer class="profile-page-actions">
            <span v-if="uploadingProfilePhoto" class="profile-upload-status">در حال بارگذاری تصویر…</span>
            <button class="profile-button profile-button-primary" type="button" :disabled="!hasProfileChanges || updatingProfile || profileLoading || uploadingProfilePhoto || !profileData.customer_id" @click="updateProfile">
              {{ updatingProfile || uploadingProfilePhoto ? 'در حال ذخیره…' : 'ذخیره تغییرات' }}
            </button>
          </footer>
        </section>
      </section>

      <div v-else-if="loading" class="loading-state">
        <div class="spinner"></div>
        <span>{{ $t('company.messages.loading') }}</span>
      </div>

      <section v-else-if="showCompaniesSection" id="companies-directory" ref="companySectionElement" class="directory-section">
        <h2 class="directory-section-title">شرکت‌ها <span>{{ filteredCompanies.length }}</span></h2>
        <div v-if="filteredCompanies.length > 0" class="chat-list">
          <div
            v-for="(company, index) in filteredCompanies"
            :key="`${company.company_id}_${company.company_type}_${index}`"
            class="chat-item"
            :class="{ 'selected-chat': selectedCompanyId === company.company_id }"
            @click="handleCompanyClick(company)"
          >
            <div class="avatar-container">
              <div v-if="!company.company_logo" class="avatar mock-avatar" :style="{ '--company-avatar-hue': companyAvatarHue(company.company_name) }" aria-hidden="true">
                {{ company.avatar_text || company.company_name?.trim()?.charAt(0) }}
              </div>
              <img v-else :src="getProxiedImage(company.company_logo)" class="avatar" />
            </div>
            <div class="chat-content">
              <div class="chat-top-row">
                <div class="chat-title-wrapper"><span class="chat-title">{{ company.company_name }}</span></div>
                <div v-if="company.company_type === 'private' || company.company_type === 'public_company'" class="company-type-badge" :class="{ 'company-type-badge--public': company.company_type === 'public_company', 'company-type-badge--private': company.company_type === 'private' }">
                  <span>{{ company.company_type === 'public_company' ? $t('company.tabs.public') : $t('company.tabs.private') }}</span>
                </div>
              </div>
              <div class="chat-bottom-row"><span class="chat-subtitle">{{ company.description || $t('company.messages.click_to_enter') }}</span></div>
            </div>
          </div>
        </div>
        <div v-else class="empty-state"><p>{{ $t('company.messages.no_companies_in_tab') }}</p></div>
      </section>

      <section v-if="activeDirectory !== 'profile' && !loading && showStoresSection" id="stores-directory" ref="storeSectionElement" class="directory-section">
        <h2 class="directory-section-title">فروشگاه‌ها <span>{{ filteredStores.length }}</span></h2>
        <div v-if="filteredStores.length > 0" class="chat-list">
          <div
            v-for="(company, index) in filteredStores"
            :key="`store_${company.company_id}_${index}`"
            class="chat-item"
            :class="{ 'selected-chat': selectedCompanyId === company.company_id }"
            @click="handleCompanyClick(company)"
          >
            <div class="avatar-container">
              <div v-if="!company.company_logo" class="avatar mock-avatar" :style="{ '--company-avatar-hue': companyAvatarHue(company.company_name) }" aria-hidden="true">{{ company.avatar_text || company.company_name?.trim()?.charAt(0) }}</div>
              <img v-else :src="getProxiedImage(company.company_logo)" class="avatar" />
            </div>
            <div class="chat-content">
              <div class="chat-top-row"><div class="chat-title-wrapper"><span class="chat-title">{{ company.company_name }}</span></div><span class="company-type-badge company-type-badge--store">{{ $t('company.fields.category_public_store_short') }}</span></div>
              <div class="chat-bottom-row"><span class="chat-subtitle">{{ company.description || $t('company.messages.click_to_enter') }}</span></div>
            </div>
          </div>
        </div>
        <div v-else class="empty-state"><p>فروشگاهی با این عبارت پیدا نشد.</p></div>
      </section>

      <section v-if="activeDirectory !== 'profile' && !loading && showAiSection" id="ai-directory" ref="aiSectionElement" class="directory-section">
        <h2 class="directory-section-title">هوش مصنوعی <span>{{ filteredAiCompanies.length }}</span></h2>
      <div class="ai-companies-grid">
        <div 
          v-for="company in filteredAiCompanies"
          :key="company.company_id"
          class="ai-company-card"
          @click="openAIWebsite(company)"
        >
          <div class="ai-company-avatar">
            <img :src="`https://ui-avatars.com/api/?name=${company.company_name}&background=0A66C2&color=fff&font-size=0.3`" />
          </div>

          <div class="ai-company-info">
            <span class="ai-company-name">{{ company.company_name }}</span>
            <span class="ai-company-desc">
              {{ company.description || $t('company.ai.default_description') }}
            </span>
          </div>

          <svg class="external-link-icon" viewBox="0 0 24 24" width="18" height="18">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
            <polyline points="15 3 21 3 21 9"></polyline>
            <line x1="10" y1="14" x2="21" y2="3"></line>
          </svg>
        </div>
        <div v-if="filteredAiCompanies.length === 0" class="empty-state">
          <p>{{ labels.aiNoResults }}</p>
        </div>
      </div>
      </section>
    </div>
    <div v-if="activePanel === 'settings'" class="mobile-panel-overlay" @click.self="activePanel = null">
      <section class="mobile-panel" role="dialog" aria-modal="true" :aria-label="labels.settings">
        <div class="mobile-panel-heading">
          <h2>{{ labels.settings }}</h2>
          <button type="button" class="mobile-panel-close" :aria-label="labels.close" @click="activePanel = null">×</button>
        </div>

        <div class="settings-options">
          <label class="filter-field">
            <span>{{ labels.language }}</span>
            <select v-model="currentLanguage" @change="changeLanguage">
              <option value="fa">فارسی</option>
              <option value="en">English</option>
              <option value="ar">العربية</option>
            </select>
          </label>
          <button type="button" class="settings-theme-action" @click="toggleTheme">
            <span>{{ labels.theme }}</span>
            <span>{{ currentTheme === 'dark' ? labels.dark : labels.light }}</span>
          </button>
        </div>
      </section>
    </div>

    <!-- <button v-if="activeTab !== 'ai' && isCompanyListVisible" type="button" class="mobile-add-company-fab" :title="$t('company.create.title')" :aria-label="$t('company.create.title')" @click="openCreateCompanyModal">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"></path></svg>
    </button> -->
    <nav class="mobile-bottom-nav" :aria-label="labels.companies">
      <button type="button" class="bottom-nav-item" :class="{ active: activeDirectory === 'profile' }" @click="navigateBottom('profile')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="12" cy="8" r="4"></circle><path d="M4 21a8 8 0 0 1 16 0"></path>
        </svg>
        <span>پروفایل</span>
      </button>
      <button type="button" class="bottom-nav-item" :class="{ active: activeDirectory === 'stores' }" @click="navigateBottom('stores')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="3"></circle><path d="m19.4 15 .1.1 1.4 1.1-1.4 2.4-1.7-.7a8 8 0 0 1-1.5.9l-.3 1.8h-2.8l-.3-1.8a8 8 0 0 1-1.5-.9l-1.7.7-1.4-2.4 1.4-1.1a8 8 0 0 1 0-1.8l-1.4-1.1 1.4-2.4 1.7.7a8 8 0 0 1 1.5-.9l.3-1.8h2.8l.3 1.8a8 8 0 0 1 1.5.9l1.7-.7 1.4 2.4-1.4 1.1a8 8 0 0 1 0 1.8Z"></path>
        </svg>
        <span>فروشگاه‌ها</span>
      </button>
      <button type="button" class="bottom-nav-item" :class="{ active: activeDirectory === 'companies' }" @click="navigateBottom('companies')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M3 21h18M5 21V5l7-3v19M19 21V9l-7-4"></path><path d="M8 7h1m-1 4h1m-1 4h1m7-2h1m-1 4h1"></path>
        </svg>
        <span>شرکتها</span>
      </button>
      <button type="button" class="bottom-nav-item" :class="{ active: activeDirectory === 'ai' }" @click="navigateBottom('ai')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="m12 3 1.6 5.4L19 10l-5.4 1.6L12 17l-1.6-5.4L5 10l5.4-1.6L12 3Zm7 13 .8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8L19 16ZM5 2l.7 2.3L8 5l-2.3.7L5 8l-.7-2.3L2 5l2.3-.7L5 2Z"></path>
        </svg>
        <span>هوش مصنوعی</span>
        <small class="bottom-nav-count">{{ aiCompanies.length }}</small>
      </button>
    </nav>
  </div>
</template>
<script setup>
import { ref, onMounted, computed, onUnmounted, nextTick, reactive } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { authService, companyService, profileService } from '@/services/api'
import RegisterationModal from '@/components/RegisterationModal.vue'
import nmp from "@neshan-maps-platform/mapbox-gl"
import navbarLogo from '@/assets/Logo4.svg'

const loadingFeaturedId = ref(null)
const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

// UI compatibility state used by the imported SelectCompany template.
// The existing company/auth logic below remains unchanged.
const currentLanguage = ref(localStorage.getItem('lang') || 'fa')
const languageMenuOpen = ref(false)
const languageOptions = [
  { value: 'fa', label: 'فارسی', flag: '🇮🇷' },
  { value: 'en', label: 'English', flag: '🇺🇸' },
  { value: 'ar', label: 'العربية', flag: '🇸🇦' },
]
const activeLanguageOption = computed(() => languageOptions.find(({ value }) => value === currentLanguage.value) || languageOptions[0])
const currentTheme = ref(document.documentElement.getAttribute('data-theme') || localStorage.getItem('theme') || 'light')
const labels = new Proxy({}, { get: (_, key) => key })
const showcaseLabels = new Proxy({}, { get: (_, key) => key })
const showcaseCarouselEdges = reactive({ about: { left: false, right: false }, companies: { left: false, right: false }, stores: { left: false, right: false } })
const featuredCompanies = computed(() =>
  companies.value
    .filter(c => Boolean(c.is_feature_company) && !String(c.company_type || '').includes('store'))
    .slice(0, 8)
  
);

const featuredStores = computed(() =>
  companies.value
    .filter(c => Boolean(c.is_feature_store) && String(c.company_type || '').includes('store'))
    .slice(0, 8)
);

const filteredAiCompanies = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase()
  if (!query) return aiCompanies.value
  return aiCompanies.value.filter(company =>
    `${company.company_name || ''} ${company.description || ''}`.toLocaleLowerCase().includes(query)
  )
})
const searchQuery = ref('')
const activeDirectory = ref('companies')
const directoryTypeFilter = ref('all')
const companySectionElement = ref(null)
const storeSectionElement = ref(null)
const aiSectionElement = ref(null)
const showFilterPanel = ref(false)
const companyFilterType = ref('all')
const activePanel = ref(null)
const profileInitial = computed(() => (authStore.user?.first_name || authStore.user?.name || 'U').charAt(0).toUpperCase())
const profileData = ref({ customer_id: '', first_name: '', last_name: '', email: '', phone_number: '', national_code: '', address: '', postal_code: '', mohal_kar: '', profile_photo_url: '' })
const originalProfileData = ref(null)
const profileError = ref('')
const profileMessage = ref('')
const updatingProfile = ref(false)
const uploadingProfilePhoto = ref(false)
const profilePhotoFile = ref(null)
const profilePhotoPreview = ref('')
const profilePhotoInput = ref(null)
const profileLoading = ref(false)
// const companyCarousel = ref(null)
// const companyListElement = ref(null)
// const isCompanyListVisible = computed(() => true)
const hasProfileChanges = computed(() => Boolean(originalProfileData.value) &&
  (Boolean(profilePhotoFile.value) || Object.keys(profileData.value).some(key => String(profileData.value[key] ?? '') !== String(originalProfileData.value[key] ?? ''))))

const changeLanguage = (language) => {
  currentLanguage.value = typeof language === 'string' ? language : language?.target?.value || 'fa'
  localStorage.setItem('lang', currentLanguage.value)
  document.documentElement.setAttribute('lang', currentLanguage.value)
  document.documentElement.setAttribute('dir', currentLanguage.value === 'en' ? 'ltr' : 'rtl')
  languageMenuOpen.value = false
}
const toggleTheme = () => {
  currentTheme.value = currentTheme.value === 'dark' ? 'light' : 'dark'
  document.documentElement.setAttribute('data-theme', currentTheme.value)
  localStorage.setItem('theme', currentTheme.value)
  localStorage.setItem('landing-theme', currentTheme.value)
}
const clearFilters = () => {
  searchQuery.value = ''
  directoryTypeFilter.value = 'all'
  companyFilterType.value = 'all'
}
const activeFilterCount = computed(() =>
  Number(directoryTypeFilter.value !== 'all') + Number(companyFilterType.value !== 'all')
)
const showCompaniesSection = computed(() => directoryTypeFilter.value === 'all' || directoryTypeFilter.value === 'companies')
const showStoresSection = computed(() => directoryTypeFilter.value === 'all' || directoryTypeFilter.value === 'stores')
const showAiSection = computed(() => directoryTypeFilter.value === 'all' || directoryTypeFilter.value === 'ai')
const companyAvatarHue = (name) => `${Math.abs(String(name || '').split('').reduce((sum, char) => sum + char.charCodeAt(0), 0)) % 360}`
const loadCustomerProfile = async () => {
  profileLoading.value = true
  profileError.value = ''
  profileMessage.value = ''
  try {
    const response = await profileService.getCustomerProfile()
    if (!response.data?.success || !response.data?.customer_data) throw new Error(response.data?.message || 'دریافت پروفایل ناموفق بود')
    const customer = response.data.customer_data
    profileData.value = {
      customer_id: String(customer.id || ''),
      first_name: customer.first_name || '',
      last_name: customer.last_name || '',
      email: customer.email || '',
      phone_number: customer.phone_number || '',
      national_code: customer.national_code || '',
      address: customer.address || '',
      postal_code: customer.postal_code || '',
      mohal_kar: customer.mohal_kar || '',
      profile_photo_url: customer.profile_photo_url || '',
    }
    profilePhotoFile.value = null
    profilePhotoPreview.value = customer.profile_photo_url || ''
    originalProfileData.value = { ...profileData.value }
  } catch (error) {
    profileError.value = error.response?.data?.detail || error.message || 'دریافت پروفایل ناموفق بود.'
  } finally {
    profileLoading.value = false
  }
}
const openProfile = () => {
  const profileWasAlreadyOpen = activeDirectory.value === 'profile'
  activeDirectory.value = 'profile'
  activeTab.value = 'all'
  activePanel.value = null
  if (!profileWasAlreadyOpen || !originalProfileData.value) loadCustomerProfile()
}
const openProfilePhotoPicker = () => {
  if (profileLoading.value || updatingProfile.value || uploadingProfilePhoto.value) return
  profilePhotoInput.value?.click()
}
const setProfilePhoto = (file) => {
  if (!file) return
  if (!file.type?.startsWith('image/')) {
    profileError.value = 'لطفاً یک فایل تصویری انتخاب کنید.'
    return
  }
  if (file.size > 5 * 1024 * 1024) {
    profileError.value = 'حجم تصویر نباید بیشتر از ۵ مگابایت باشد.'
    return
  }
  if (profilePhotoPreview.value.startsWith('blob:')) URL.revokeObjectURL(profilePhotoPreview.value)
  profilePhotoFile.value = file
  profilePhotoPreview.value = URL.createObjectURL(file)
  profileError.value = ''
  profileMessage.value = ''
}
const handleProfilePhotoChange = (event) => {
  setProfilePhoto(event.target.files?.[0])
  event.target.value = ''
}
const handleProfilePhotoDrop = (event) => setProfilePhoto(event.dataTransfer?.files?.[0])
const updateProfile = async () => {
  if (!hasProfileChanges.value) return
  updatingProfile.value = true
  profileError.value = ''
  profileMessage.value = ''
  try {
    const profileUpdate = { ...profileData.value }
    delete profileUpdate.profile_photo_url
    const response = await profileService.updateProfile(profileUpdate)
    if (!response.data?.success) throw new Error(response.data?.message || 'ذخیره پروفایل ناموفق بود')

    if (profilePhotoFile.value) {
      uploadingProfilePhoto.value = true
      const formData = new FormData()
      formData.append('file', profilePhotoFile.value)
      const photoResponse = await profileService.uploadProfilePhoto(formData)
      if (!photoResponse.data?.success || !photoResponse.data?.profile_photo_url) {
        throw new Error(photoResponse.data?.message || 'بارگذاری تصویر پروفایل ناموفق بود')
      }
      profileData.value.profile_photo_url = photoResponse.data.profile_photo_url
      if (profilePhotoPreview.value.startsWith('blob:')) URL.revokeObjectURL(profilePhotoPreview.value)
      profilePhotoPreview.value = photoResponse.data.profile_photo_url
      profilePhotoFile.value = null
    }

    originalProfileData.value = { ...profileData.value }
    profileMessage.value = 'تغییرات پروفایل ذخیره شد.'
    if (authStore.user) {
      authStore.user.first_name = profileData.value.first_name
      authStore.user.last_name = profileData.value.last_name
    }
  } catch (error) {
    profileError.value = error.response?.data?.detail || error.message || 'ذخیره پروفایل ناموفق بود.'
  } finally {
    uploadingProfilePhoto.value = false
    updatingProfile.value = false
  }
}
const navigateBottom = (destination) => {
  if (destination === 'profile') {
    openProfile()
    return
  }
  const sections = {
    companies: companySectionElement,
    stores: storeSectionElement,
    ai: aiSectionElement,
  }
  const target = sections[destination]
  if (!target) return
  activePanel.value = null
  directoryTypeFilter.value = 'all'
  activeTab.value = 'all'
  activeDirectory.value = destination
  nextTick(() => target.value?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
}
const moveShowcaseCarousel = () => {}
const pauseShowcaseAutoplay = () => {}
const resumeShowcaseAutoplay = () => {}
const syncShowcaseCarousel = () => {}
const showcaseCarouselEdgeClasses = () => ({})
const showCustomerRegistrationModal = ref(false)
const customerRegistrationProfile = ref(null)
const registrationCheckLoading = ref(true)
const registrationCheckError = ref('')

// Refs
const logoInput = ref(null)
const serviceImageInputs = ref([])
const locationMapElement = ref(null)

let locationMap = null
let locationMarker = null

// State
const loading = ref(false)
const companies = ref([])
const selectedCompanyId = ref(null)
const autoSelectAttempted = ref(false)
const errorMessage = ref('')
const parentSelectionModal = ref(false)
const parentMatches = ref([])
const selectedParentSubcode = ref('')
const pendingParentCompany = ref(null)
const parentSelectionError = ref('')
const activeTab = ref('all')
const showCreateModal = ref(false)
const creatingCompany = ref(false)
const locationSearch = ref('')
const isSwitching = ref(false)

const showSwitchConfirmModal = ref(false)
const pendingCompany = ref(null)

// Validation errors
const validationErrors = ref({})

// Form data
const formData = ref({
  company_name: '',
  national_id: '',
  economic_id: '',
  activity_type: '',
  description: '',
  complete_description: '',
  country: 'ایران',
  vahed_pool: 1,
  multi_arz: true,
  company_category: 'private',
  store_category: 'private',
  active: true,
  company_website_url: '',
  company_address: '',
  post_code: '',
  location_lat: null,
  location_lng: null,
  services: [
    { title: '', description: '' }
  ]
})

// File data
const logoFile = ref(null)
const serviceImageFiles = ref([])
const logoPreview = ref(null)
const serviceImagesPreview = ref([])

// AI Companies (hardcoded for now)
const aiCompanies = ref([
  {
    company_id: 'ai_1',
    company_name: 'هوش مصنوعی میزگرد',
    description: 'میزگرد برای تحقیق و توسعه',
    website: 'https://nifty-morse-jcyqjiggd.liara.run/Roundtable/Index',
    has_db_access: false
  },
  {
    company_id: 'ai_2',
    company_name: 'هوش مصنوعی مقالات',
    description: 'جست و جوی هوشمند و خلاصه سازی مقالات با هوش مصنوعی',
    website: 'https://cardiacresearcher.liara.run/',
    has_db_access: false
  }
])

const showAgreementModal = ref(false)
const agreementAccepted = ref(false)

const openAgreementModal = () => {
  if (!validateForm()) return
  showAgreementModal.value = true
}

const confirmCreateCompany = async () => {
  if (!agreementAccepted.value) return

  showAgreementModal.value = false
  await createCompany()
}

// Computed property to check if form is valid
const isFormValid = computed(() => {
  return (
    formData.value.company_name.trim() !== '' &&
    formData.value.national_id.trim() !== '' &&
    formData.value.economic_id.trim() !== '' &&
    formData.value.activity_type.trim() !== '' &&
    formData.value.country.trim() !== '' &&
    formData.value.company_address.trim() !== '' &&
    formData.value.post_code.trim() !== '' &&
    formData.value.description.trim() !== '' &&
    formData.value.company_category.trim() !== '' &&
    formData.value.store_category.trim() !== ''
  )
})

// Computed property to filter companies based on active tab
const filteredCompanies = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase()
  return companies.value.filter(company => {
    const isStore = String(company.company_type || '').includes('store')
    if (isStore) return false
    if (companyFilterType.value === 'private' && company.company_type !== 'private') return false
    if (companyFilterType.value === 'public' && company.company_type !== 'public_company') return false
    return !query || `${company.company_name || ''} ${company.description || ''}`.toLocaleLowerCase().includes(query)
  })
})

const filteredStores = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase()
  return companies.value.filter(company => {
    if (!String(company.company_type || '').includes('store')) return false
    return !query || `${company.company_name || ''} ${company.description || ''}`.toLocaleLowerCase().includes(query)
  })
})

// Logo handling
const triggerLogoUpload = () => {
  logoInput.value?.click()
}

const handleLogoUpload = (event) => {
  const file = event.target.files[0]
  if (!file) return
  
  // Validate file size (max 2MB)
  if (file.size > 2 * 1024 * 1024) {
    alert('حجم فایل باید کمتر از ۲ مگابایت باشد')
    return
  }
  
  // Validate file type
  if (!file.type.startsWith('image/')) {
    alert('فایل باید تصویر باشد')
    return
  }
  
  logoFile.value = file
  
  // Create preview
  const reader = new FileReader()
  reader.onload = (e) => {
    logoPreview.value = e.target.result
  }
  reader.readAsDataURL(file)
}

const isConnectingSupport = ref(false);

const TARGET_SUPPORT_COMPANY = {
  company_id: "5",
  company_logo: "http://5.144.129.213/uploads/companies/5/logos/f594be15-6e3d-48e3-ac86-ba058ca0e092.webp",
  company_name: "خانه نرم افزار",
  description: "تولید و توسعه نرم افزار",
  has_db_access: true,
  country: null,
  is_selected: true,
  company_category: "public",
  store_category: null,
  company_type: "public_company"
};

const handleContactUsClick = async () => {
  if (isConnectingSupport.value) return;
  isConnectingSupport.value = true;

  try {
    // ۱. بررسی وجود شرکت در لیست شرکت‌های کاربر یا استفاده از آبجکت هدف
    const existingCompany = authStore.companies?.find(c => String(c.company_id) === '5') || TARGET_SUPPORT_COMPANY;

    // ۲. ست کردن شرکت در استور
    if (typeof authStore.selectCompany === 'function') {
      await authStore.selectCompany(existingCompany.company_id, existingCompany.company_name);
    } else {
      authStore.selectedCompany = existingCompany;
    }

    // ۳. ست کردن نقش office
    if (typeof authStore.setSelectedRole === 'function') {
      authStore.setSelectedRole('office');
    }

    // ذخیره در لوکال استوریج برای پایداری داده‌ها
    localStorage.setItem('selected_company_id', '5');
    localStorage.setItem('company_type', 'public_company');
    localStorage.setItem('selected_role', 'office');

    // ۴. انتقال مستقیم به کارتابل نامه‌های اداری
    router.push('/office/office-letters');

  } catch (error) {
    console.error('Error auto-selecting support company:', error);
    // در صورت بروز خطا به همان صفحه لاگین یا نقش هدایت شود
    router.push('/office/office-letters');
  } finally {
    isConnectingSupport.value = false;
  }
};


const removeLogo = () => {
  logoFile.value = null
  logoPreview.value = null
  if (logoInput.value) {
    logoInput.value.value = ''
  }
}

// Service image handling
const triggerServiceImageUpload = (index) => {
  serviceImageInputs.value[index]?.click()
}

const handleServiceImageUpload = (event, index) => {
  const file = event.target.files[0]
  if (!file) return
  
  // Validate file size
  if (file.size > 2 * 1024 * 1024) {
    alert('حجم فایل باید کمتر از ۲ مگابایت باشد')
    return
  }
  
  // Validate file type
  if (!file.type.startsWith('image/')) {
    alert('فایل باید تصویر باشد')
    return
  }
  
  // Ensure arrays are long enough
  while (serviceImageFiles.value.length <= index) {
    serviceImageFiles.value.push(null)
    serviceImagesPreview.value.push(null)
  }
  
  serviceImageFiles.value[index] = file
  
  // Create preview
  const reader = new FileReader()
  reader.onload = (e) => {
    serviceImagesPreview.value[index] = e.target.result
  }
  reader.readAsDataURL(file)
}

const removeServiceImage = (index) => {
  if (serviceImageFiles.value[index]) {
    serviceImageFiles.value[index] = null
    serviceImagesPreview.value[index] = null
  }
  if (serviceImageInputs.value[index]) {
    serviceImageInputs.value[index].value = ''
  }
}

// Modal functions
const openCreateCompanyModal = () => {
  agreementAccepted.value = false
  showAgreementModal.value = false

  // Reset form
  formData.value = {
    company_name: '',
    national_id: '',
    economic_id: '',
    activity_type: '',
    description: '',
    complete_description: '',
    country: 'ایران',
    vahed_pool: 1,
    multi_arz: true,
    company_category: 'private',
    store_category: 'private',
    active: true,
    company_website_url: '',
    company_address: '',
    post_code: '',
    location_lat: null,
    location_lng: null,
    services: [
      { title: '', description: '' }
    ]
  }
  
  // Reset files
  logoFile.value = null
  logoPreview.value = null
  serviceImageFiles.value = []
  serviceImagesPreview.value = []
  serviceImageInputs.value = []
  
  validationErrors.value = {}
  showCreateModal.value = true
  
  // Initialize map after modal is visible
  nextTick(() => {
    initLocationPickerMap()
  })
}

const closeCreateCompanyModal = () => {
  showCreateModal.value = false
  validationErrors.value = {}
  
  // Clean up map
  if (locationMap) {
    try {
      locationMap.remove()
    } catch (e) {
      console.warn('Error removing map:', e)
    }
    locationMap = null
    locationMarker = null
  }

}

const addService = () => {
  if (formData.value.services.length < 4) {
    formData.value.services.push({ title: '', description: '' })
  }
}

const removeService = (index) => {
  if (formData.value.services.length > 1) {
    formData.value.services.splice(index, 1)
    serviceImageFiles.value.splice(index, 1)
    serviceImagesPreview.value.splice(index, 1)
  }
}

// Validate form before submission
const validateForm = () => {
  const errors = {}

  // company_name
  if (!formData.value.company_name.trim()) {
    errors.company_name = 'نام شرکت الزامی است'
  } else if (formData.value.company_name.trim().length < 2) {
    errors.company_name = 'نام شرکت باید حداقل ۲ کاراکتر باشد'
  } else if (formData.value.company_name.trim().length > 100) {
    errors.company_name = 'نام شرکت نمی‌تواند بیشتر از ۱۰۰ کاراکتر باشد'
  }

  // activity_type
  if (!formData.value.activity_type.trim()) {
    errors.activity_type = 'نوع فعالیت الزامی است'
  } else if (formData.value.activity_type.trim().length < 2) {
    errors.activity_type = 'نوع فعالیت نامعتبر است'
  }

  // country
  if (!formData.value.country.trim()) {
    errors.country = 'کشور الزامی است'
  } else if (formData.value.country.trim().length < 2) {
    errors.country = 'نام کشور نامعتبر است'
  }

  // company_address
  if (!formData.value.company_address.trim()) {
    errors.company_address = 'آدرس الزامی است'
  } else if (formData.value.company_address.trim().length < 5) {
    errors.company_address = 'آدرس بسیار کوتاه است'
  } else if (formData.value.company_address.trim().length > 250) {
    errors.company_address = 'آدرس نمی‌تواند بیشتر از ۲۵۰ کاراکتر باشد'
  }

  // company_category
  const allowedCompanyCategories = ['private', 'public', 'none']
  if (!formData.value.company_category) {
    errors.company_category = 'نوع شرکت الزامی است'
  } else if (!allowedCompanyCategories.includes(formData.value.company_category)) {
    errors.company_category = 'نوع شرکت نامعتبر است'
  }

  // store_category
  const allowedStoreCategories = ['private', 'public', 'none']
  if (!formData.value.store_category) {
    errors.store_category = 'دسته‌بندی فروشگاه الزامی است'
  } else if (!allowedStoreCategories.includes(formData.value.store_category)) {
    errors.store_category = 'دسته‌بندی فروشگاه نامعتبر است'
  }

  // coordinates if present
  if (formData.value.location_lat !== null) {
    const lat = Number(formData.value.location_lat)
    if (isNaN(lat) || lat < -90 || lat > 90) {
      errors.location_lat = 'عرض جغرافیایی باید بین -90 و 90 باشد'
    }
  }

  if (formData.value.location_lng !== null) {
    const lng = Number(formData.value.location_lng)
    if (isNaN(lng) || lng < -180 || lng > 180) {
      errors.location_lng = 'طول جغرافیایی باید بین -180 و 180 باشد'
    }
  }

  validationErrors.value = errors
  if (Object.keys(errors).length > 0) {
    scrollToError()
    return false
  }
  return true
}

// Create company function
const createCompany = async () => {
  if (!validateForm()) {
    return
  }
  
  // Filter out empty services
  const validServices = formData.value.services.filter(
    s => s.title.trim() && s.description.trim()
  )
  
  creatingCompany.value = true
  
  // Create FormData for multipart/form-data
  const formPayload = new FormData()
  
  // Add all form fields
  formPayload.append('company_name', formData.value.company_name)
  formPayload.append('national_id', formData.value.national_id)
  formPayload.append('economic_id', formData.value.economic_id)
  formPayload.append('activity_type', formData.value.activity_type || '')
  formPayload.append('description', formData.value.description || '')
  formPayload.append('complete_description', formData.value.complete_description || '')
  formPayload.append('country', formData.value.country)
  formPayload.append('vahed_pool', formData.value.vahed_pool.toString())
  formPayload.append('multi_arz', formData.value.multi_arz ? 'true' : 'false')
  formPayload.append('company_category', formData.value.company_category)
  formPayload.append('active', formData.value.active ? 'true' : 'false')
  formPayload.append('store_category', formData.value.store_category || '')
  formPayload.append('company_website_url', formData.value.company_website_url || '')
  formPayload.append('company_address', formData.value.company_address || '')
  formPayload.append('post_code', formData.value.post_code || '')
  
  if (formData.value.location_lat) {
    formPayload.append('location_lat', formData.value.location_lat.toString())
  }
  if (formData.value.location_lng) {
    formPayload.append('location_lng', formData.value.location_lng.toString())
  }
  
  // Add services as JSON
  formPayload.append('services_json', JSON.stringify(validServices))
  
  // Add logo if exists
  if (logoFile.value) {
    formPayload.append('company_logo', logoFile.value)
  }
  
  // Add service images
  for (let i = 0; i < Math.min(validServices.length, 4); i++) {
    if (serviceImageFiles.value[i]) {
      formPayload.append(`service_image${i + 1}`, serviceImageFiles.value[i])
    }
  }
  
  console.log('FormData contents:', Array.from(formPayload.entries()))
  try {
    const response = await companyService.createCompany(formPayload)
    
    if (response.data.success) {
      alert(`شرکت ${formData.value.company_name} با موفقیت ایجاد شد`)
      closeCreateCompanyModal()
      
      // Refresh companies list
      await loadCompanies()
      
      // Switch to the appropriate tab based on company category
      if (formData.value.company_category === 'private') {
        activeTab.value = 'private'
      } else if (formData.value.company_category === 'public_company') {
        activeTab.value = 'public'
      } else if (formData.value.company_category === 'public_store') {
        activeTab.value = 'store'
      }
    }
  } catch (error) {
    console.error('Error creating company:', error)
    alert('خطا در ایجاد شرکت: ' + (error.response?.data?.detail || error.message))
  } finally {
    creatingCompany.value = false
  }
}

// Load companies from auth store
const loadCompanies = async () => {
  loading.value = true
  try {
    companies.value = authStore.companies || []
    console.log('Companies loaded:', companies.value)
  } catch (error) {
    console.error('Error loading companies:', error)
  } finally {
    loading.value = false
  }
}

// Open AI company website
const openAIWebsite = (company) => {
  if (company.website) {
    //window.open(company.website, '_blank')
  } else {
    alert('لینک وبسایت موجود نیست')
  }
}

const selectCompany = async (company, selectedSubcode = null) => {
  if (registrationCheckLoading.value || registrationCheckError.value || showCustomerRegistrationModal.value) return
  try {
    loading.value = true
    errorMessage.value = ''
    
    // Check if this is a store
    const isStore = company.company_type === 'public_store' || company.company_type === 'private_store'
    
    let response
    
    if (isStore) {
      response = await authStore.selectStoreWithPreAuth(company.company_id)
    } else {
      // For regular companies
      response = await authStore.selectCompany(company.company_id, company.company_name, selectedSubcode)
    }

    if (!isStore && response?.requires_parent_selection) {
      pendingParentCompany.value = company
      parentMatches.value = response.parent_matches || []
      selectedParentSubcode.value = ''
      parentSelectionError.value = ''
      parentSelectionModal.value = true
      return
    }

    if (!isStore && response?.success && customerRegistrationProfile.value) {
      try {
        const profile = customerRegistrationProfile.value
        const syncResponse = await authService.registerCustomer({
          name: [profile.first_name, profile.last_name].filter(Boolean).join(' ') || profile.name,
          phone: profile.phone,
          national_code: profile.national_code,
          address: profile.address,
          postal_code: profile.postal_code,
          mohal_kar: profile.mohal_kar,
        })
        const companyCustomerId = syncResponse.data.customer_id
        if (syncResponse.data.success && companyCustomerId &&
            String(authStore.user?.subCodeId || '') !== String(companyCustomerId)) {
          response = await authStore.selectCompany(company.company_id, company.company_name, selectedSubcode)
        }
      } catch (syncError) {
        console.error('Customer profile saved, but company sync failed:', syncError)
      }
    }
    
    if (response && response.success) {
      // Store the access type for routing
      localStorage.setItem('company_access_type', company.access_type || 'private')
      localStorage.setItem('company_type', company.company_type)

      if (company.company_name === 'باربری پیک'){
        authStore.setIsPeak()
      }
      
      if (isStore){
        authStore.setSelectedRole('store')
        await router.push('/sales/kala-store')
      } else {
        await router.push('/select-role')
      }
    }
  } catch (error) {
    // Error handling...
  } finally {
    loading.value = false
  }
}

const cancelParentSelection = () => {
  parentSelectionModal.value = false
  pendingParentCompany.value = null
  parentMatches.value = []
  selectedParentSubcode.value = ''
  parentSelectionError.value = ''
}

const continueWithSelectedParent = async () => {
  if (!pendingParentCompany.value || !selectedParentSubcode.value) {
    parentSelectionError.value = 'لطفاً یک کد را انتخاب کنید.'
    return
  }
  const company = pendingParentCompany.value
  const subcode = selectedParentSubcode.value
  parentSelectionModal.value = false
  parentSelectionError.value = ''
  await selectCompany(company, subcode)
}

const onCustomerRegistrationCompleted = (result) => {
  customerRegistrationProfile.value = result?.profile || result?.registration || null
  registrationCheckError.value = ''
}

const checkCustomerRegistration = async () => {
  registrationCheckLoading.value = true
  registrationCheckError.value = ''
  const roles = (authStore.user?.roles || []).map(role => String(role).toLowerCase())
  const activeRole = String(authStore.selectedRole || authStore.user?.primaryRole || '').toLowerCase()
  if (!roles.includes('customer') && activeRole !== 'customer') {
    registrationCheckLoading.value = false
    return
  }

  try {
    const response = await authService.getCustomerRegistrationStatus()
    customerRegistrationProfile.value = response.data.profile || null
    showCustomerRegistrationModal.value = !response.data.registration_complete
  } catch (error) {
    registrationCheckError.value = error.response?.data?.detail
      || 'امکان بررسی تکمیل اطلاعات حساب وجود ندارد. لطفاً دوباره تلاش کنید.'
    console.error('Could not check customer registration on company selection:', error)
  } finally {
    registrationCheckLoading.value = false
  }
}

// Logout
const logout = async () => {
  await authStore.logout()
  router.push('/login')
}

const getProxiedImage = (url) => {
  const proxied = authStore.proxiedImage(url)
  return proxied
}

const setLocation = (lat, lng) => {
  formData.value.location_lat = Number(lat)
  formData.value.location_lng = Number(lng)

  if (!locationMap) return

  // move map center
  try {
    locationMap.flyTo({
      center: [Number(lng), Number(lat)],
      zoom: 15
    })
  } catch (e) {
    console.warn('flyTo failed:', e)
  }

  addLocationMarker(Number(lat), Number(lng))
}

const initLocationPickerMap = () => {
  if (!locationMapElement.value) return

  if (typeof nmp === 'undefined') {
    setTimeout(() => {
      initLocationPickerMap()
    }, 500)
    return
  }

  // destroy previous map if modal reopened
  if (locationMap) {
    try {
      locationMap.remove()
    } catch (e) {
      console.warn('Error removing old map:', e)
    }
    locationMap = null
    locationMarker = null
  }

  const centerLat = formData.value.location_lat || 35.6892
  const centerLng = formData.value.location_lng || 51.3890

  try {
    locationMap = new nmp.Map({
      mapKey: 'web.73a02790728f4a38862451622ccb92d7',
      container: locationMapElement.value,
      center: [centerLng, centerLat],
      zoom: 13,
      mapType: 'neshanVectorNight'
    })

    locationMap.on('load', () => {
      // important when map is inside modal
      setTimeout(() => {
        try {
          locationMap.resize()
        } catch (e) {
          console.warn('Map resize failed:', e)
        }

        if (formData.value.location_lat && formData.value.location_lng) {
          addLocationMarker(formData.value.location_lat, formData.value.location_lng)
        }
      }, 200)
    })

    locationMap.on('click', (e) => {
      const { lng, lat } = e.lngLat
      setLocation(lat, lng)
    })

    locationMap.on('error', (e) => {
      if (e?.error?.message?.includes('message channel closed')) {
        return
      }
      console.error('Map error:', e)
    })
  } catch (error) {
    if (!error?.message?.includes('message channel closed')) {
      console.error('Error initializing map:', error)
    }
  }
}

const addLocationMarker = (lat, lng) => {
  if (!locationMap) return

  try {
    if (locationMarker) {
      locationMarker.setLngLat([Number(lng), Number(lat)])
      return
    }

    locationMarker = new nmp.Marker({
      draggable: true
    })
      .setLngLat([Number(lng), Number(lat)])
      .addTo(locationMap)

    locationMarker.on('dragend', () => {
      const pos = locationMarker.getLngLat()
      formData.value.location_lat = Number(pos.lat)
      formData.value.location_lng = Number(pos.lng)
    })
  } catch (error) {
    console.error('Error adding marker:', error)
  }
}

const clearLocation = () => {
  formData.value.location_lat = null
  formData.value.location_lng = null

  if (locationMarker) {
    try {
      locationMarker.remove()
    } catch (e) {
      console.warn('Error removing marker:', e)
    }
    locationMarker = null
  }

  if (locationMap) {
    try {
      locationMap.flyTo({
        center: [51.3890, 35.6892],
        zoom: 11
      })
    } catch (e) {
      console.warn('Error resetting map center:', e)
    }
  }
}

const searchLocation = async () => {
  if (!locationSearch.value.trim()) return

  try {
    const query = encodeURIComponent(locationSearch.value.trim())

    // using Nominatim geocoding
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1`,
      {
        headers: {
          Accept: 'application/json'
        }
      }
    )

    const data = await response.json()

    if (Array.isArray(data) && data.length > 0) {
      const lat = parseFloat(data[0].lat)
      const lng = parseFloat(data[0].lon)
      setLocation(lat, lng)
    } else {
      alert('مکان مورد نظر یافت نشد')
    }
  } catch (error) {
    console.error('Location search error:', error)
    alert('خطا در جستجوی مکان')
  }
}

const errorRefs = ref({})

const scrollToError = () => {
  nextTick(() => {
    const firstErrorField = Object.keys(validationErrors.value)[0]
    if (firstErrorField) {
      const element = document.getElementById(`field-${firstErrorField}`)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  })
}

// Helper functions for cart management
const clearCartItems = () => {
    localStorage.removeItem('shoppingCart')
}

const hasCartItems = () => {
    try {
        const cart = localStorage.getItem('shoppingCart')
        if (!cart) return false
        const items = JSON.parse(cart)
        return items && items.length > 0
    } catch (e) {
        return false
    }
}

const handleCompanyClick = (company) => {
  console.log('company '+company)
    // Check if trying to switch to a different company
    const currentCompanyId = authStore.selectedCompany?.company_id
    const isSameCompany = currentCompanyId == company.company_id
    
    // If it's the same company, just select it
    if (isSameCompany) {
        return selectCompany(company)
    }
    
    // If cart has items, show confirmation modal
    if (hasCartItems()) {
        pendingCompany.value = company
        showSwitchConfirmModal.value = true
        return
    } else {
        // No items in cart, proceed with switch
        return selectCompany(company)
    }
}

const closeSwitchConfirmModal = () => {
    showSwitchConfirmModal.value = false
    pendingCompany.value = null
}

const confirmSwitchCompany = async () => {
    if (!pendingCompany.value) return
    
    // Clear the cart from localStorage
    clearCartItems()
    
    // Close modal
    showSwitchConfirmModal.value = false
    const company = pendingCompany.value
    pendingCompany.value = null
    
    // Proceed with company selection
    await selectCompany(company)
}

// Clean up on unmount
onUnmounted(() => {
  if (profilePhotoPreview.value.startsWith('blob:')) URL.revokeObjectURL(profilePhotoPreview.value)
  if (locationMap) {
    try {
      locationMap.remove()
    } catch (e) {
      console.warn('Error removing map on unmount:', e)
    }
    locationMap = null
    locationMarker = null
  }
})

const handleFeaturedClick = async (company) => {
  if (loadingFeaturedId.value) return
  loadingFeaturedId.value = company.company_id
  try {
    await handleCompanyClick(company)
  } catch (e) {
    console.error(e)
  } finally {
    loadingFeaturedId.value = null
  }
}

// Lifecycle
onMounted(async () => {
  await checkCustomerRegistration()
  await loadCompanies()

  if (showCustomerRegistrationModal.value || registrationCheckError.value) return
  
  const targetCompanyId = route.query.companyId
  if (targetCompanyId && !autoSelectAttempted.value) {
    autoSelectAttempted.value = true
    const company = companies.value.find(c => c.company_id == targetCompanyId)
    if (company && company.has_db_access) {
      await selectCompany(company)
      return
    }
  }
  
  if (authStore.selectedCompany) {
    selectedCompanyId.value = authStore.selectedCompany.company_id
  }
})
</script>
<style scoped>
/* Base Messenger Styles */
.switching-note {
  max-width: 800px;
  margin: 20px auto;
  padding: 15px;
  background: var(--gradient-1);
  border-radius: 8px;
  text-align: center;
  color: var(--text-primary);
  font-weight: 500;
}

.messenger-page {
  background-color: var(--bg);
  color: var(--text);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Header */
.app-header {
  background-color: var(--card-bg);
  border-bottom: 1px solid var(--border);
  position: sticky;
  top: 0;
  z-index: 10;
  box-shadow: 0 1px 3px var(--shadow-1);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
}

.header-content h2 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--heading);
}

.btn-logout {
  background: none;
  border: none;
  color: var(--orange-2);
  font-weight: 600;
  cursor: pointer;
}

/* Tabs Styles */
.tabs-container {
  max-width: 800px;
  margin: 20px auto 10px;
  width: 100%;
  padding: 0 15px;
}

.tabs {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 5px;
  scrollbar-width: none;
}

.tabs::-webkit-scrollbar {
  display: none;
}

.tab-btn {
  padding: 8px 16px;
  border: none;
  background: var(--card-bg);
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--muted);
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--card-border);
  transition: all 0.2s ease;
}

.tab-btn:hover {
  background: var(--hover);
}

.tab-btn.active {
  background: var(--primary);
  color: var(--text-primary);
  border-color: var(--primary);
}

.tab-count {
  background: var(--card-bg);
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 0.75rem;
  color: var(--text);
}

.tab-count.zero-count {
  background: var(--border);
  color: var(--muted);
}

.tab-btn.active .tab-count {
  background: var(--ghost-background);
}

/* AI Companies Grid */
.ai-companies-grid {
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
  padding: 20px 15px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
}

.ai-company-card {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid var(--card-border);
  position: relative;
  overflow: hidden;
}

.ai-company-card:hover {
  border-color: var(--primary);
  background: var(--ring);
  transform: translateY(-4px);
  box-shadow: 0 8px 16px var(--shadow-1);
}

.ai-company-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--gradient-1), var(--gradient-2));
  opacity: 0;
  transition: opacity 0.3s ease;
}

.ai-company-card:hover::before {
  opacity: 1;
}

.ai-company-avatar {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: 0 4px 8px var(--shadow-1);
}

.ai-company-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ai-company-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.ai-company-name {
  font-weight: 700;
  font-size: 1rem;
  color: var(--heading);
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ai-company-desc {
  font-size: 0.8rem;
  color: var(--muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.external-link-icon {
  color: var(--primary);
  flex-shrink: 0;
  opacity: 0.6;
  transition: opacity 0.3s ease;
}

.ai-company-card:hover .external-link-icon {
  opacity: 1;
}

/* Chat List */
.chat-list-container {
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
  flex: 1;
  padding: 0 15px;
}

.directory-section {
  scroll-margin-top: 24px;
  margin: 0 0 28px;
  padding: 18px;
  border: 1px solid var(--glass-border);
  border-radius: 18px;
  background: var(--card-background);
}

.directory-section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 14px;
  color: var(--heading-text);
  font-size: 1.1rem;
}

.directory-section-title span {
  display: inline-grid;
  min-width: 28px;
  height: 28px;
  place-items: center;
  border-radius: 999px;
  background: var(--ghost-background);
  color: var(--text-secondary);
  font-size: .8rem;
}

.store-directory-search {
  width: 100%;
  margin-bottom: 12px;
}

@media (max-width: 600px) {
  .directory-section { padding: 13px; margin-bottom: 18px; }
}

.chat-item {
  display: flex;

  align-items: center;
  padding: 12px 15px;
  border-bottom: 1px solid var(--card-border);
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  position: relative;
  border-radius: 12px;
  margin-bottom: 4px;
  background: var(--card-bg);
}

.chat-item:hover {
  background-color: var(--hover);
  transform: translateX(-4px);
}

.chat-item:active {
  background-color: var(--border);
}

.selected-chat {
  background-color: var(--ring);
}

.selected-chat:hover {
  background-color: var(--hover);
}

/* Company Type Borders */
.public-company {
  border-right: 4px solid var(--green);
}

.public-store {
  border-right: 4px solid var(--orange-1);
}

.private-company {
  border-right: 4px solid var(--muted-2);
}

/* Avatar */
.avatar-container {
  margin-left: 15px;
  flex-shrink: 0;
  position: relative;
}

.avatar {
  width: 55px;
  height: 55px;
  border-radius: 50%;
  object-fit: contain;
  border: 2px solid var(--accent);
  transition: border-color 0.2s ease;
}

.chat-item:hover .avatar {
  border-color: var(--primary);
}

.avatar-badge {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  color: var(--text-primary);
  border: 2px solid var(--card-bg);
  box-shadow: 0 2px 4px var(--shadow-1);
}

.badge-public {
  background-color: var(--green);
}

.badge-store {
  background-color: var(--orange-1);
}

/* Chat Content */
.chat-title {
  text-align: right;
  font-weight: bold;
  font-size: 1.05rem;
  color: var(--heading);
}

.chat-subtitle {
  color: var(--muted);
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  justify-content: flex-start; /* Aligns content to the right */
  align-items: center;      /* Vertically centers the icon with the text */
  gap: 8px;                 /* Optional: adds space between the title and icon */
}

.company-category-tag {
  font-size: 0.7rem;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.tag-public {
  background-color: var(--green);
  color: var(--text-primary);
}

.tag-store {
  background-color: var(--orange-1);
  color: var(--text-primary);
}

.tag-private {
  background-color: var(--muted-2);
  color: var(--text-primary);
}

/* Empty & Loading */
.loading-state, 
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--muted);
  background: var(--card-bg);
  border-radius: 16px;
  margin: 20px 0;
  border: 1px solid var(--card-border);
}

.spinner {
  border: 3px solid var(--border);
  border-top: 3px solid var(--primary);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

/* Buttons */
.btn-primary {
  background-color: var(--primary);
  color: var(--text-primary);
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: bold;
  margin-top: 15px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.btn-primary:hover {
  background-color: var(--primary-hover);
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .tabs {
    gap: 4px;
  }
  
  .tab-btn {
    padding: 6px 12px;
    font-size: 0.8rem;
  }
  
  .ai-companies-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .avatar {
    width: 45px;
    height: 45px;
  }
  
  .chat-title {
    font-size: 0.95rem;
  }
  
  .chat-subtitle {
    font-size: 0.8rem;
  }
}

/* Animation for tab switching */
.tab-content-enter-active,
.tab-content-leave-active {
  transition: opacity 0.3s ease;
}

.tab-content-enter-from,
.tab-content-leave-to {
  opacity: 0;
}

.btn-add-company {
  background: none;
  border: none;
  color: var(--accent);
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  margin: 0 10px;
}

.btn-add-company:hover {
  background-color: var(--interactive-hover);
  transform: scale(1.1);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--modal-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  backdrop-filter: blur(5px);
  animation: fadeIn 0.3s ease;
}

.modal-content {
  background-color: var(--bg);
  border-radius: 20px;
  padding: 30px;
  max-width: 700px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px var(--glass-shadow);
  animation: slideUp 0.3s ease;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--glass-border);
}

.modal-header h3 {
  margin: 0;
  font-size: 1.5rem;
  color: var(--heading-text);
}

.modal-close {
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: var(--text-muted);
  transition: color 0.3s;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.modal-close:hover {
  color: var(--accent);
  background: var(--ghost-background);
}

.create-company-form {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.form-section {
  background: var(--card-background);
  padding: 20px;
  border-radius: 12px;
}

.form-section h4 {
  margin: 0 0 15px 0;
  color: var(--heading-text);
  font-size: 1.1rem;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--glass-border);
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  color: var(--text-secondary);
  font-weight: 500;
  font-size: 0.9rem;
}

.form-group input[type="text"],
.form-group input[type="number"],
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 10px 12px;
  border: 2px solid var(--control-border);
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.3s;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--chart-glow);
}

.form-group.checkbox {
  display: flex;
  align-items: center;
}

.form-group.checkbox label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  margin-bottom: 0;
}

.form-group.checkbox input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.form-row {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
}

.form-row .half {
  flex: 1;
}

.service-item {
  background: var(--card-bg);
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 15px;
  border: 1px solid var(--glass-border);
  position: relative;
}

.service-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.service-header span {
  font-weight: 600;
  color: var(--heading-text);
}

.remove-service-btn {
  background: none;
  border: none;
  color: var(--accent);
  font-size: 20px;
  cursor: pointer;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s;
}

.remove-service-btn:hover {
  background: var(--ghost-background);
  transform: scale(1.1);
}

.add-service-btn {
  background: none;
  border: 2px dashed var(--accent);
  color: var(--accent);
  padding: 10px;
  border-radius: 8px;
  width: 100%;
  cursor: pointer;
  font-size: 0.95rem;
  transition: all 0.3s;
}

.add-service-btn:hover {
  background: var(--interactive-hover);
  border-color: var(--accent-soft);
}

.modal-actions {
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 20px;
}

.btn-primary {
  background: linear-gradient(135deg, var(--gradient-1), var(--gradient-2));
  color: var(--text-primary);
  border: none;
  padding: 12px 30px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px var(--chart-glow);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--card-background);
  color: var(--text-muted);
  border: none;
  padding: 12px 30px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-secondary:hover {
  background: var(--glass-border);
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .modal-content {
    padding: 20px;
    width: 95%;
  }
  
  .form-row {
    flex-direction: column;
    gap: 10px;
  }
  
  .modal-actions {
    flex-direction: column;
  }
  
  .modal-actions button {
    width: 100%;
  }
}

.required-notice {
  margin-bottom: 15px;
  font-size: 0.9rem;
  color: var(--text-muted);
  text-align: left;
}

.required-star {
  color: var(--accent);
  font-weight: bold;
}

.form-group.required label {
  font-weight: 600;
}

.form-group.required label .required-star {
  margin-right: 4px;
}

.form-group input.error,
.form-group textarea.error,
.form-group select.error {
  border-color: var(--accent);
  background-color: var(--ghost-background);
  color: var(--text-primary);
}

.error-message {
  color: var(--accent);
  font-size: 0.8rem;
  margin-top: 4px;
  display: block;
}

.form-hint {
  color: var(--text-muted);
  font-size: 0.75rem;
  margin-top: 4px;
}

/* Rest of your existing styles */
.btn-add-company {
  background: none;
  border: none;
  color: var(--accent);
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  margin: 0 10px;
}

.btn-add-company:hover {
  background-color: var(--interactive-hover);
  transform: scale(1.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--glass-border);
}

.modal-header h3 {
  margin: 0;
  font-size: 1.5rem;
  color: var(--heading-text);
}

.modal-close {
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: var(--text-muted);
  transition: color 0.3s;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.modal-close:hover {
  color: var(--accent);
  background: var(--ghost-background);
}

.create-company-form {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.form-section {
  background: var(--card-bg);
  padding: 20px;
  border-radius: 12px;
}

.form-section h4 {
  margin: 0 0 15px 0;
  color: var(--text);
  font-size: 1.1rem;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--glass-border);
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  color: var(--text-secondary);
  font-weight: 500;
  font-size: 0.9rem;
}

.form-group input[type="text"],
.form-group input[type="number"],
.form-group input[type="url"],
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 10px 12px;
  border: 2px solid var(--control-border);
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.3s;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--chart-glow);
}

.form-group.checkbox {
  display: flex;
  align-items: center;
}

.form-group.checkbox label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  margin-bottom: 0;
}

.form-group.checkbox input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.form-row {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
}

.form-row .half {
  flex: 1;
}

.service-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.service-header span {
  font-weight: 600;
  color: var(--text);
}

.remove-service-btn {
  background: none;
  border: none;
  color: var(--accent);
  font-size: 20px;
  cursor: pointer;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s;
}

.remove-service-btn:hover {
  background: var(--ghost-background);
  transform: scale(1.1);
}

.add-service-btn {
  background: none;
  border: 2px dashed var(--accent);
  color: var(--accent);
  padding: 10px;
  border-radius: 8px;
  width: 100%;
  cursor: pointer;
  font-size: 0.95rem;
  transition: all 0.3s;
}

.add-service-btn:hover {
  background: var(--interactive-hover);
  border-color: var(--accent-soft);
}

.modal-actions {
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 20px;
}

.btn-primary {
  background: linear-gradient(135deg, var(--gradient-1), var(--gradient-2));
  color: var(--text-primary);
  border: none;
  padding: 12px 30px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px var(--chart-glow);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--card-background);
  color: var(--text-muted);
  border: none;
  padding: 12px 30px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-secondary:hover {
  background: var(--glass-border);
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 600px) {
  
  .form-row {
    flex-direction: column;
    gap: 10px;
  }
  
  .modal-actions {
    flex-direction: column;
  }
  
  .modal-actions button {
    width: 100%;
  }
}
.file-upload-container {
  margin: 10px 0;
}

.file-upload-container.small {
  max-width: 200px;
}

.file-upload-placeholder {
  border: 2px dashed var(--glass-border-strong);
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background: var(--card-background);
}

.file-upload-placeholder:hover {
  border-color: var(--accent);
  background: var(--interactive-hover);
}

.file-upload-placeholder svg {
  color: var(--text-muted);
  margin-bottom: 10px;
}

.file-upload-placeholder span {
  display: block;
  color: var(--text-muted);
  font-size: 0.9rem;
}

.file-upload-preview {
  position: relative;
  display: inline-block;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid var(--glass-border);
}

.file-upload-preview img {
  max-width: 200px;
  max-height: 200px;
  display: block;
}

.remove-file {
  position: absolute;
  top: 5px;
  right: 5px;
  background: var(--accent-border);
  color: var(--text-primary);
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.remove-file:hover {
  background: var(--accent);
  transform: scale(1.1);
}

.map-container {
  margin: 15px 0;
  border: 2px solid var(--glass-border);
  border-radius: 8px;
  overflow: hidden;
}

.location-map-container {
  width: 100%;
  height: 300px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 16px;
  border: 1px solid var(--card-border);
}

.location-map {
  width: 100%;
  height: 100%;
}

.location-coordinates {
  display: flex;
  gap: 15px;
  align-items: center;
  margin-top: 10px;
  padding: 10px;
  background: var(--card-background);
  border-radius: 8px;
}

.location-coordinates span {
  color: var(--heading-text);
  font-size: 0.9rem;
}

.location-search {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.location-search input {
  flex: 1;
  padding: 8px 12px;
  border: 2px solid var(--control-border);
  border-radius: 6px;
  font-size: 0.9rem;
}

.location-search input:focus {
  outline: none;
  border-color: var(--accent);
}

.btn-small {
  padding: 6px 12px;
  background: var(--control-border);
  border: none;
  border-radius: 4px;
  color: var(--text-secondary);
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-small:hover {
  background: var(--glass-border-strong);
}

.text-colored {
  color: var(--text);
}

.agreement-modal{
  max-height:90vh;
  overflow:hidden;
  display:flex;
  flex-direction:column;
}

.agreement-content{
  overflow-y:auto;
  max-height:50vh;
  padding:10px;
  line-height:1.8;
}

.agreement-checkbox{
  margin-top:15px;
  font-weight:500;
}
.chat-title-wrapper {
  display: flex;
  justify-content: flex-start; /* Aligns content to the right */
  align-items: center;      /* Vertically centers the icon with the text */
  gap: 8px;                 /* Optional: adds space between the title and icon */
}
/* Confirmation Modal Styles */
.confirm-modal {
    max-width: 450px;
}

.confirm-content {
    padding: 20px 24px;
    text-align: center;
}

.confirm-icon {
    font-size: 64px;
    margin-bottom: 16px;
    display: block;
}

.confirm-content p {
    font-size: 16px;
    color: var(--text);
    margin-bottom: 8px;
    line-height: 1.6;
}

.confirm-detail {
    font-size: 14px;
    color: var(--subtext);
}

.btn-danger {
    padding: 10px 24px;
    background: var(--orange-1);
    color: var(--text-primary);
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    transition: all 0.3s ease;
}

.btn-danger:hover {
    background: var(--orange-2);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px var(--chart-glow);
}

.modal-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    padding: 16px 24px;
    border-top: 1px solid var(--card-border);
}

/* Responsive */
@media (max-width: 480px) {
    .confirm-modal {
        max-width: 100%;
        margin: 0 16px;
    }
    
    .modal-actions {
        flex-direction: column;
    }
    
    .btn-danger,
    .btn-secondary {
        width: 100%;
        justify-content: center;
    }
}

/* Select Company theme polish */
.messenger-page {
  --bg: var(--surface);
  --card-bg: var(--card-background);
  --text: var(--text-primary);
  --subtext: var(--text-secondary);
  --heading: var(--heading-text);
  --muted: var(--text-muted);
  --muted-2: var(--text-muted);
  --card-border: var(--glass-border);
  --border: var(--glass-border);
  --ring: var(--interactive-hover);
  --shadow-1: var(--glass-shadow);
  --shadow-2: var(--card-shadow);
  --shadow-3: var(--card-shadow);
  --hover: var(--interactive-hover);
  position: relative;
  isolation: isolate;
  min-height: 100vh;
  color: var(--text-primary);
  background: var(--page-background, var(--background));
}

.messenger-page::before,
.messenger-page::after {
  content: "";
  position: fixed;
  z-index: -1;
  width: clamp(18rem, 36vw, 34rem);
  aspect-ratio: 1;
  border-radius: 50%;
  filter: blur(52px);
  pointer-events: none;
  opacity: 0.85;
}

.messenger-page::before {
  top: -13rem;
  left: -12rem;
  background: radial-gradient(circle, var(--dashboard-glow), transparent 68%);
}

.messenger-page::after {
  right: -12rem;
  bottom: -16rem;
  background: radial-gradient(circle, var(--chart-glow), transparent 68%);
}

.app-header {
  background: var(--nav-background);
  border-bottom: 1px solid var(--glass-border);
  box-shadow: 0 12px 32px var(--glass-shadow);
  -webkit-backdrop-filter: blur(20px);
  backdrop-filter: blur(20px);
}

.header-content {
  max-width: 1100px;
  padding: 14px 22px;
}

.navbar-brand {
  padding: 8px 14px;
  border: 1px solid var(--glass-border);
  border-radius: 14px;
  background: var(--nav-brand-background);
  color: var(--brand-text);
  font-weight: 700;
  text-decoration: none;
}

.btn-back,
.btn-add-company,
.btn-logout {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 42px;
  min-height: 42px;
  padding: 9px;
  border: 1px solid var(--glass-border);
  border-radius: 14px;
  background: var(--ghost-background);
  color: var(--accent);
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease;
}

.btn-back:hover,
.btn-add-company:hover,
.btn-logout:hover {
  transform: translateY(-2px);
  border-color: var(--accent-border);
  background: var(--interactive-hover);
}

.btn-logout img {
  filter: grayscale(1) brightness(1.8);
}

.tabs-container {
  max-width: 1100px;
  margin: 34px auto 18px;
  padding: 0 22px;
}

.tabs {
  gap: 10px;
  padding: 8px;
  border: 1px solid var(--glass-border);
  border-radius: 20px;
  background: var(--surface-strong);
  box-shadow: 0 14px 34px var(--glass-shadow);
  -webkit-backdrop-filter: blur(18px);
  backdrop-filter: blur(18px);
}

.tab-btn {
  flex: 1 0 auto;
  justify-content: center;
  min-height: 44px;
  padding: 9px 18px;
  border: 1px solid transparent;
  border-radius: 14px;
  background: transparent;
  color: var(--text-muted);
  transition: color 0.2s ease, background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.tab-btn:hover {
  background: var(--interactive-hover);
  color: var(--text-primary);
}

.tab-btn.active {
  border-color: var(--accent-border);
  background: linear-gradient(135deg, var(--accent), var(--accent-soft));
  color: var(--surface);
  box-shadow: 0 8px 22px var(--chart-glow);
}

.tab-count,
.tab-count.zero-count,
.tab-btn.active .tab-count {
  border: 1px solid var(--glass-border);
  background: var(--ghost-background);
  color: var(--text-primary);
}

.chat-list-container {
  max-width: 1100px;
  padding: 0 22px 44px;
}

.chat-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 340px), 1fr));
  gap: 14px;
}

.chat-item {
  min-width: 0;
  min-height: 104px;
  margin: 0;
  padding: 18px 20px;
  border: 1px solid var(--glass-border);
  border-right: 3px solid var(--text-muted);
  border-radius: 18px;
  background: var(--card-background);
  box-shadow: 0 12px 30px var(--glass-shadow);
  -webkit-backdrop-filter: blur(18px);
  backdrop-filter: blur(18px);
  transition: transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease, background 0.22s ease;
}

.chat-item:hover {
  transform: translateY(-3px);
  background: var(--surface-strong);
  box-shadow: 0 18px 36px var(--card-shadow), 0 0 24px var(--chart-glow);
}

.chat-item:active {
  transform: translateY(-1px);
  background: var(--surface-hover);
}

.chat-item.public-company { border-right-color: var(--green); }
.chat-item.public-store { border-right-color: var(--accent); }
.chat-item.private-company { border-right-color: var(--text-muted); }

.chat-item.selected-chat {
  border-color: var(--accent-border);
  background: var(--surface-strong);
  box-shadow: 0 0 0 1px var(--accent-border), 0 16px 34px var(--card-shadow), 0 0 26px var(--chart-glow);
}

.avatar {
  border: 2px solid var(--glass-border-strong);
  background: var(--control-background-muted);
}

.chat-item:hover .avatar {
  border-color: var(--accent-border);
}

.avatar-badge {
  width: auto;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-color: var(--card-background);
  border-radius: 999px;
  font-size: 0.65rem;
}

.badge-public,
.tag-public {
  background: var(--green);
  color: var(--surface);
}

.badge-store,
.tag-store {
  background: var(--accent);
  color: var(--surface);
}

.tag-private {
  border: 1px solid var(--glass-border);
  background: var(--surface-hover);
  color: var(--text-primary);
}

.chat-title {
  color: var(--heading-text);
}

.chat-subtitle {
  color: var(--text-muted);
}

.icon-tv,
.external-link-icon {
  color: var(--accent);
  stroke: currentColor;
}

.ai-company-card,
.loading-state,
.empty-state {
  border: 1px solid var(--glass-border);
  background: var(--card-background);
  box-shadow: 0 14px 34px var(--glass-shadow);
  -webkit-backdrop-filter: blur(18px);
  backdrop-filter: blur(18px);
}

.ai-company-card:hover {
  border-color: var(--accent-border);
  background: var(--surface-strong);
  box-shadow: 0 18px 36px var(--card-shadow), 0 0 24px var(--chart-glow);
}

.ai-company-card::before {
  background: linear-gradient(90deg, var(--accent), var(--accent-soft));
}

.ai-company-name { color: var(--heading-text); }
.ai-company-desc { color: var(--text-muted); }

.spinner {
  border-color: var(--glass-border-strong);
  border-top-color: var(--accent);
}

.modal-overlay {
  background: var(--modal-overlay);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
}

.modal-content {
  border: 1px solid var(--glass-border-strong);
  background: var(--card-background);
  color: var(--text-primary);
  box-shadow: 0 24px 64px var(--card-shadow), 0 0 34px var(--chart-glow);
  -webkit-backdrop-filter: blur(24px);
  backdrop-filter: blur(24px);
}

.modal-header {
  border-bottom-color: var(--glass-border);
}

.modal-header h3,
.form-section h4,
.form-group label {
  color: var(--text-primary);
}

.form-section {
  border: 1px solid var(--glass-border);
  background: var(--surface-strong);
}

.form-section h4 {
  border-bottom-color: var(--glass-border);
}

.messenger-page .form-group input:not([type="checkbox"]):not([type="file"]),
.messenger-page .form-group textarea,
.messenger-page .form-group select {
  border: 1px solid var(--control-border);
  background: var(--control-background);
  color: var(--text-primary);
}

.messenger-page .form-group select option {
  background: var(--menu-background);
  color: var(--text-primary);
}

.messenger-page .form-group input:focus,
.messenger-page .form-group textarea:focus,
.messenger-page .form-group select:focus {
  border-color: var(--accent-border);
  box-shadow: 0 0 0 3px var(--chart-glow);
}

.messenger-page .form-group input.error,
.messenger-page .form-group textarea.error,
.messenger-page .form-group select.error {
  border-color: var(--accent);
  background: var(--control-background-muted);
  color: var(--text-primary);
}

.required-notice,
.form-hint,
.confirm-detail { color: var(--text-muted); }
.required-star,
.error-message { color: var(--accent); }

.btn-primary,
.messenger-page .btn-primary {
  border: 1px solid var(--accent-border);
  background: linear-gradient(135deg, var(--accent), var(--accent-soft));
  color: var(--surface);
  box-shadow: 0 8px 22px var(--chart-glow);
}

.btn-primary:hover:not(:disabled),
.btn-danger:hover {
  filter: brightness(1.06);
  transform: translateY(-2px);
  box-shadow: 0 12px 26px var(--chart-glow);
}

.btn-secondary {
  border: 1px solid var(--glass-border);
  background: var(--ghost-background);
  color: var(--text-secondary);
}

.btn-secondary:hover {
  background: var(--interactive-hover);
  color: var(--text-primary);
}

.btn-add-company { color: var(--accent); }

@media (max-width: 600px) {
  .header-content { padding: 12px 14px; }
  .navbar-brand { padding: 7px 10px; font-size: 0.92rem; }
  .tabs-container { margin-top: 22px; padding: 0 14px; }
  .tabs { gap: 6px; padding: 6px; }
  .tab-btn { padding: 8px 12px; }
  .chat-list-container { padding: 0 14px 30px; }
  .chat-item { padding: 15px; }
}


.mock-avatar {
  display: grid;
  place-items: center;
  font-size: 1.15rem;
  font-weight: 800;
  color: var(--accent);
}

.chat-item.public-company .mock-avatar { border-color: var(--green); color: var(--green); }
.chat-item.public-store .mock-avatar { border-color: var(--accent); color: var(--accent); }
.chat-item.private-company .mock-avatar { border-color: var(--text-muted); color: var(--text-muted); }
/* Keep the create-company dialogs in the same theme as the selector. */
.service-item,
.file-upload-placeholder,
.location-coordinates {
  border: 1px solid var(--glass-border);
  background: var(--control-background-muted);
  color: var(--text-secondary);
}

.service-item { border-radius: 14px; }
.file-upload-placeholder { border-style: dashed; }
.file-upload-placeholder:hover,
.add-service-btn:hover {
  border-color: var(--accent-border);
  background: var(--interactive-hover);
}

.file-upload-placeholder svg,
.file-upload-placeholder span,
.location-coordinates span { color: var(--text-secondary); }
.file-upload-preview { border-color: var(--glass-border-strong); }

.remove-file,
.remove-service-btn {
  border: 1px solid var(--glass-border);
  background: var(--surface-strong);
  color: var(--accent);
}

.remove-file:hover,
.remove-service-btn:hover,
.modal-close:hover {
  background: var(--interactive-hover);
  color: var(--accent);
}

.add-service-btn {
  border: 1px dashed var(--accent-border);
  background: var(--ghost-background);
  color: var(--accent);
}

.map-container,
.location-map-container { border-color: var(--glass-border); }
.location-search input {
  border: 1px solid var(--control-border);
  background: var(--control-background);
  color: var(--text-primary);
}
.location-search input:focus {
  border-color: var(--accent-border);
  box-shadow: 0 0 0 3px var(--chart-glow);
}

.btn-small {
  border: 1px solid var(--glass-border);
  background: var(--ghost-background);
  color: var(--text-primary);
}
.btn-small:hover { background: var(--interactive-hover); }

.modal-close {
  border: 1px solid var(--glass-border);
  color: var(--text-muted);
}

.btn-danger {
  border: 1px solid var(--accent-border);
  background: linear-gradient(135deg, var(--accent), var(--accent-soft));
  color: var(--surface);
}

/* Narrow mobile-app composition */
.messenger-page {
  min-height: 100dvh;
  padding-bottom: calc(28px + env(safe-area-inset-bottom, 0px));
}

.app-header { padding-top: env(safe-area-inset-top, 0px); }
.header-content {
  max-width: 560px;
  min-height: 72px;
  justify-content: flex-start;
  gap: 10px;
}

.header-content .navbar-brand {
  order: 0;
  margin: 0 !important;
  white-space: nowrap;
}

.header-content > .logo-icon {
  order: 1;
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
}

.header-content > .logo-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.header-content > .btn-add-company {
  order: 2;
  margin-inline-start: auto;
}

.header-content > .btn-back { order: 3; }
.header-content > .btn-logout { order: 4; }

.tabs-container,
.chat-list-container {
  max-width: 560px;
}

.tabs-container {
  margin: 16px auto 14px;
  padding: 0 16px;
}

.tabs {
  justify-content: flex-start;
  gap: 8px;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scroll-snap-type: x proximity;
}

.tab-btn {
  flex: 0 0 auto;
  scroll-snap-align: start;
  min-height: 42px;
  padding: 8px 15px;
}

.chat-list-container { padding: 0 16px 34px; }

.chat-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.chat-item {
  width: 100%;
  min-height: 112px;
  gap: 12px;
  padding: 16px;
  border-right: 1px solid var(--glass-border);
  border-left: 3px solid var(--text-muted);
  border-radius: 20px;
}

.chat-item.public-company { border-left-color: var(--green); }
.chat-item.public-store { border-left-color: var(--accent); }
.chat-item.private-company { border-left-color: var(--text-muted); }

.avatar-container {
  flex: 0 0 auto;
  margin-left: 0;
}

.avatar {
  width: 64px;
  height: 64px;
}

.chat-content {
  flex: 1;
  min-width: 0;
}

.chat-title-wrapper {
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;
}

.chat-title {
  font-size: 1rem;
  line-height: 1.45;
}

.icon-tv {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
}

.chat-subtitle {
  display: -webkit-box;
  overflow: hidden;
  white-space: normal;
  line-height: 1.55;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.company-entry-arrow {
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  margin-inline-start: 4px;
  color: var(--text-muted);
  transition: transform 0.2s ease, color 0.2s ease;
}

.chat-item:hover .company-entry-arrow {
  transform: translateX(-3px);
  color: var(--accent);
}

.ai-companies-grid {
  max-width: 560px;
  grid-template-columns: 1fr;
  padding: 8px 0;
}

@media (max-width: 420px) {
  .header-content { gap: 7px; padding: 11px 12px; }
  .header-content > .logo-icon { width: 36px; height: 36px; flex-basis: 36px; }
  .header-content .navbar-brand { padding: 7px 10px; font-size: 0.88rem; }
  .btn-back,
  .btn-add-company,
  .btn-logout { min-width: 38px; min-height: 38px; padding: 7px; }
  .tabs-container { padding: 0 12px; }
  .chat-list-container { padding-right: 12px; padding-left: 12px; }
  .chat-item { min-height: 100px; gap: 9px; padding: 13px 11px; }
  .avatar { width: 56px; height: 56px; }
  .chat-title { font-size: 0.94rem; }
  .company-category-tag { padding: 2px 7px; font-size: 0.64rem; }
}

/* Mobile directory controls and persistent app navigation */
.messenger-page { padding-bottom: calc(112px + env(safe-area-inset-bottom, 0px)); }
.header-content { max-width: 560px; min-height: 66px; justify-content: space-between; gap: 10px; padding: 10px 14px; }
.header-brand, .header-actions { display: flex; align-items: center; }
.header-brand { gap: 8px; min-width: 0; }
.header-brand .logo-icon { width: 38px; height: 38px; flex: 0 0 38px; }
.header-brand img.logo-icon { width: 100%; height: 100%; object-fit: contain; }
.header-brand .navbar-brand { margin: 0 !important; padding: 7px 10px; font-size: 0.9rem; white-space: nowrap; }
.header-actions { gap: 7px; flex: 0 0 auto; }
.header-actions .btn-logout, .header-actions .btn-add-company, .theme-toggle { min-width: 38px; min-height: 38px; padding: 7px; }
.language-select { width: 66px; min-width: 0; height: 38px; padding: 4px 7px; border: 1px solid var(--control-border); border-radius: 12px; background: var(--control-background); color: var(--text-primary); font-size: 0.78rem; }
.language-select option { background: var(--menu-background); color: var(--text-primary); }
.theme-toggle { display: inline-flex; align-items: center; justify-content: center; border: 1px solid var(--glass-border); border-radius: 12px; background: var(--ghost-background); color: var(--accent); cursor: pointer; }
.theme-toggle:hover { background: var(--interactive-hover); border-color: var(--accent-border); }

.directory-controls { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: center; gap: 9px; width: min(100%, 560px); margin: 14px auto 10px; padding: 0 16px; }
.directory-search { display: flex; align-items: center; gap: 9px; min-width: 0; min-height: 46px; padding: 0 13px; border: 1px solid var(--glass-border); border-radius: 15px; background: var(--control-background); color: var(--text-muted); }
.directory-search:focus-within { border-color: var(--accent-border); box-shadow: 0 0 0 3px var(--chart-glow); }
.directory-search input { width: 100%; min-width: 0; border: 0; outline: 0; background: transparent; color: var(--text-primary); font: inherit; text-align: start; }
.directory-search input::placeholder { color: var(--text-muted); }
.filter-toggle { display: inline-flex; align-items: center; justify-content: center; gap: 7px; min-height: 46px; padding: 0 12px; border: 1px solid var(--glass-border); border-radius: 15px; background: var(--surface-strong); color: var(--text-primary); white-space: nowrap; cursor: pointer; }
.filter-toggle:hover, .filter-toggle[aria-expanded="true"] { border-color: var(--accent-border); color: var(--accent); }
.filter-count, .bottom-nav-count { display: inline-grid; place-items: center; min-width: 19px; height: 19px; padding: 0 5px; border-radius: 999px; background: var(--accent); color: var(--surface); font-size: 0.68rem; font-weight: 800; }
.company-filter-panel { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; width: min(calc(100% - 32px), 528px); margin: 0 auto 12px; padding: 16px; border: 1px solid var(--glass-border-strong); border-radius: 20px; background: var(--surface-strong); box-shadow: 0 16px 36px var(--glass-shadow); -webkit-backdrop-filter: blur(18px); backdrop-filter: blur(18px); }
.filter-panel-heading { display: flex; align-items: center; justify-content: space-between; grid-column: 1 / -1; }
.filter-panel-heading h2 { margin: 0; color: var(--heading-text); font-size: 1rem; }
.filter-close, .mobile-panel-close { display: grid; place-items: center; width: 34px; height: 34px; border: 1px solid var(--glass-border); border-radius: 11px; background: var(--ghost-background); color: var(--text-secondary); font-size: 1.2rem; cursor: pointer; }
.filter-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; color: var(--text-secondary); font-size: 0.82rem; }
.filter-field select, .filter-field input { width: 100%; min-height: 42px; padding: 8px 10px; border: 1px solid var(--control-border); border-radius: 12px; background: var(--control-background); color: var(--text-primary); font: inherit; text-align: start; }
.filter-field select option { background: var(--menu-background); color: var(--text-primary); }
.clear-filters { grid-column: 1 / -1; min-height: 40px; border: 1px solid var(--accent-border); border-radius: 12px; background: var(--ghost-background); color: var(--accent); font: inherit; cursor: pointer; }

.mobile-bottom-nav { position: fixed; z-index: 80; bottom: 0; left: 50%; display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); width: min(100%, 560px); padding: 8px 10px calc(8px + env(safe-area-inset-bottom, 0px)); transform: translateX(-50%); border: 1px solid var(--glass-border-strong); border-bottom: 0; border-radius: 22px 22px 0 0; background: var(--surface-strong); box-shadow: 0 -12px 36px var(--glass-shadow); -webkit-backdrop-filter: blur(22px); backdrop-filter: blur(22px); }
.bottom-nav-item { position: relative; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; min-width: 0; min-height: 54px; padding: 4px 2px; border: 0; border-radius: 14px; background: transparent; color: var(--text-muted); font: inherit; font-size: 0.7rem; cursor: pointer; }
.bottom-nav-item svg { width: 21px; height: 21px; }
.bottom-nav-item.active { background: var(--ghost-background); color: var(--accent); }
.bottom-nav-item.active span { font-weight: 700; }
.bottom-nav-count { position: absolute; top: 1px; right: calc(50% - 25px); min-width: 16px; height: 16px; font-size: 0.6rem; }

.mobile-panel-overlay { position: fixed; z-index: 90; inset: 0; display: flex; align-items: flex-end; justify-content: center; padding: 12px 12px calc(74px + env(safe-area-inset-bottom, 0px)); background: var(--modal-overlay); -webkit-backdrop-filter: blur(8px); backdrop-filter: blur(8px); }
.mobile-panel { width: min(100%, 560px); max-height: 78vh; overflow: auto; padding: 20px; border: 1px solid var(--glass-border-strong); border-radius: 24px; background: var(--card-background); color: var(--text-primary); box-shadow: 0 24px 60px var(--card-shadow); }
.mobile-panel-heading { display: flex; align-items: center; justify-content: space-between; margin-bottom: 18px; }
.mobile-panel-heading h2 { margin: 0; color: var(--heading-text); font-size: 1.12rem; }
.profile-summary { display: grid; justify-items: center; gap: 14px; padding: 8px 0 4px; }
.profile-summary-avatar { display: grid; place-items: center; width: 68px; height: 68px; border: 1px solid var(--accent-border); border-radius: 50%; background: var(--ghost-background); color: var(--accent); font-size: 1.4rem; font-weight: 800; }
.profile-summary-name { color: var(--text-primary); font-size: 1.05rem; }
.profile-detail { display: flex; justify-content: space-between; gap: 16px; width: 100%; padding: 12px 0; border-top: 1px solid var(--glass-border); color: var(--text-secondary); text-align: start; }
.profile-detail span:last-child { overflow-wrap: anywhere; text-align: end; }
.settings-options { display: grid; gap: 14px; }
.settings-theme-action { display: flex; justify-content: space-between; align-items: center; min-height: 46px; padding: 10px 12px; border: 1px solid var(--glass-border); border-radius: 12px; background: var(--ghost-background); color: var(--text-primary); text-align: start; cursor: pointer; }

@media (max-width: 420px) {
  .header-content { align-items: flex-start; flex-direction: column; gap: 8px; }
  .header-brand { width: 100%; justify-content: flex-start; }
  .header-actions { width: 100%; justify-content: space-between; gap: 6px; }
  .company-filter-panel { grid-template-columns: 1fr; }
  .filter-panel-heading, .clear-filters { grid-column: 1; }
  .directory-controls { gap: 7px; padding-right: 12px; padding-left: 12px; }
  .filter-toggle { gap: 5px; padding: 0 9px; }
}

/* Select Company header inspired by the site's floating glass navbar */
.select-company-header {
  top: 0;
  padding: calc(env(safe-area-inset-top, 0px) + 10px) 12px 7px;
  border: 0;
  background: transparent;
  box-shadow: none;
  -webkit-backdrop-filter: none;
  backdrop-filter: none;
}

.select-company-nav {
  width: min(100%, 560px);
  min-height: 68px;
  justify-content: space-between;
  gap: 12px;
  margin: 0 auto;
  padding: 8px 11px;
  border: 1px solid var(--glass-border-strong);
  border-radius: 23px;
  background: var(--nav-background);
  box-shadow: 0 12px 34px var(--glass-shadow);
  -webkit-backdrop-filter: blur(20px);
  backdrop-filter: blur(20px);
}

.select-company-nav .header-brand {
  gap: 10px;
  flex: 0 1 auto;
}

.select-company-logo {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  border: 1px solid var(--accent-border);
  border-radius: 15px;
  background: var(--control-background);
  box-shadow: 0 0 18px var(--chart-glow);
}

.select-company-logo-image {
  width: 78%;
  height: 78%;
  object-fit: contain;
}

.select-company-brand-copy {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.select-company-nav .navbar-brand {
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--heading-text);
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.35;
}

.select-company-brand-caption {
  color: var(--text-muted);
  font-size: 0.72rem;
  line-height: 1.25;
}

.select-company-nav .header-actions {
  align-items: center;
  gap: 6px;
}

.select-company-nav .btn-logout,
.select-company-nav .btn-add-company,
.select-company-nav .theme-toggle {
  display: inline-grid;
  place-items: center;
  width: 40px;
  min-width: 40px;
  height: 40px;
  min-height: 40px;
  flex: 0 0 40px;
  margin: 0;
  padding: 8px;
  border: 1px solid var(--control-border);
  border-radius: 13px;
  background: var(--control-background);
  color: var(--text-primary);
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease, color 0.2s ease;
}

.select-company-nav .btn-logout:hover,
.select-company-nav .btn-add-company:hover,
.select-company-nav .theme-toggle:hover {
  transform: translateY(-1px);
  border-color: var(--accent-border);
  background: var(--interactive-hover);
  color: var(--accent);
}

.select-company-nav .btn-add-company {
  color: var(--accent);
}

.company-language-picker {
  position: relative;
  flex: 0 0 auto;
}

.company-language-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 84px;
  height: 40px;
  padding: 0 9px;
  border: 1px solid var(--control-border);
  border-radius: 13px;
  background: var(--control-background);
  color: var(--text-primary);
  font: inherit;
  font-size: 0.76rem;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.company-language-trigger:hover,
.company-language-trigger[aria-expanded="true"] {
  border-color: var(--accent-border);
  background: var(--interactive-hover);
}

.company-language-flag {
  font-size: 1rem;
  line-height: 1;
}

.company-language-name {
  white-space: nowrap;
}

.company-language-chevron {
  width: 14px;
  height: 14px;
  color: var(--text-muted);
  transition: transform 0.2s ease;
}

.company-language-trigger[aria-expanded="true"] .company-language-chevron {
  transform: rotate(180deg);
}

.company-language-menu {
  position: absolute;
  z-index: 100;
  top: calc(100% + 8px);
  inset-inline-end: 0;
  display: grid;
  min-width: 148px;
  gap: 4px;
  padding: 7px;
  border: 1px solid var(--glass-border-strong);
  border-radius: 15px;
  background: var(--menu-background);
  box-shadow: 0 16px 36px var(--glass-shadow);
  -webkit-backdrop-filter: blur(18px);
  backdrop-filter: blur(18px);
}

.company-language-option {
  display: flex;
  align-items: center;
  gap: 9px;
  min-height: 38px;
  padding: 6px 9px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: var(--text-primary);
  font: inherit;
  text-align: start;
  cursor: pointer;
}

.company-language-option:hover,
.company-language-option[aria-selected="true"] {
  background: var(--ghost-background);
  color: var(--accent);
}

@media (max-width: 420px) {
  .select-company-header {
    padding-right: 10px;
    padding-left: 10px;
  }

  .select-company-nav {
    align-items: stretch;
    flex-direction: column;
    gap: 8px;
    padding: 9px 11px 10px;
    border-radius: 21px;
  }

  .select-company-nav .header-brand {
    width: 100%;
  }

  .select-company-logo {
    width: 40px;
    height: 40px;
    flex-basis: 40px;
  }

  .select-company-nav .header-actions {
    width: 100%;
    justify-content: space-between;
    gap: 6px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .select-company-nav *,
  .select-company-nav *::before,
  .select-company-nav *::after {
    transition-duration: 0.01ms !important;
  }
}

/* Registration navbar language/theme controls and company brand, adapted to this view */
.select-company-nav .select-company-brand {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.select-company-brand .nav-brand {
  display: inline-flex;
  width: 62px;
  height: 42px;
  flex: 0 0 62px;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
}

.select-company-brand .nav-brand:hover {
  transform: translateY(-2px);
}

.select-company-brand .nav-brand img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.select-company-brand .almas-text {
  margin: 0;
  color: var(--heading-text);
  font-size: 0.93rem;
  font-weight: 900;
  white-space: nowrap;
}

.select-company-nav .company-language-trigger {
  min-width: 112px;
  min-height: 42px;
  height: 42px;
  justify-content: space-between;
  padding: 0 9px;
  border-radius: 13px;
  font-size: 0.78rem;
}

.company-language-trigger[aria-expanded="true"] .company-language-icon {
  transform: rotate(180deg);
}

.company-language-icon {
  width: 15px;
  height: 15px;
  flex: 0 0 15px;
  object-fit: contain;
  opacity: 0.8;
  filter: brightness(0) invert(1);
  transition: transform 0.2s ease;
}

.select-company-nav .theme-toggle {
  width: 42px;
  min-width: 42px;
  height: 42px;
  min-height: 42px;
  flex-basis: 42px;
  border-radius: 12px;
}

.theme-glyph {
  font-size: 1.35rem;
  line-height: 1;
}

.ai-directory-controls {
  grid-template-columns: minmax(0, 1fr);
}

.profile-modal-overlay {
  position: fixed;
  z-index: 110;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 16px;
  background: var(--modal-overlay);
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
}

.profile-modal {
  position: relative;
  display: grid;
  gap: 14px;
  width: min(460px, 100%);
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  padding: 24px;
  border: 1px solid var(--glass-border-strong);
  border-radius: 22px;
  background: var(--menu-background);
  color: var(--text-primary);
  box-shadow: 0 24px 70px var(--glass-shadow);
}

.profile-modal-header,
.profile-modal-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.profile-modal-header h2 {
  margin: 0;
  color: var(--heading-text);
  font-size: 1.15rem;
}

.profile-modal .modal-close {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border: 1px solid var(--glass-border);
  border-radius: 11px;
  background: var(--ghost-background);
  color: var(--text-primary);
  cursor: pointer;
  font-size: 1.4rem;
}

.profile-modal .profile-avatar {
  display: grid;
  place-items: center;
  width: 54px;
  height: 54px;
  margin-bottom: 2px;
  border-radius: 50%;
  background: var(--accent);
  color: var(--surface);
  font-size: 1.25rem;
  font-weight: 800;
}

.profile-modal label {
  display: grid;
  gap: 6px;
  color: var(--text-secondary);
  font-size: 0.78rem;
}

.profile-modal input,
.profile-modal textarea {
  width: 100%;
  min-height: 42px;
  padding: 0 11px;
  border: 1px solid var(--control-border);
  border-radius: 10px;
  background: var(--control-background);
  color: var(--text-primary);
  font: inherit;
}

.profile-modal input:disabled,
.profile-modal textarea:disabled {
  opacity: 0.65;
}

.profile-form-message { margin: 0; text-align: center; font-size: .85rem; }
.profile-form-message.is-error { color: #dc2626; }
.profile-form-message.is-success { color: #15803d; }

.chat-list-container.is-profile-view { width: min(100%, 1120px); max-width: 1120px; padding: 8px 24px 48px; }
.profile-page { width: 100%; }
.profile-page-heading { display: flex; align-items: flex-end; justify-content: space-between; gap: 18px; margin: 8px 0 20px; }
.profile-page-heading h1 { margin: 5px 0; color: var(--heading-text); font-size: clamp(1.4rem, 3vw, 1.9rem); }
.profile-page-heading p { margin: 0; color: var(--text-secondary); font-size: .88rem; }
.profile-page-eyebrow { color: var(--accent); font-size: .77rem; font-weight: 800; }
.profile-page-breadcrumb { color: var(--text-secondary); font-size: .8rem; white-space: nowrap; }
.profile-page-breadcrumb b { padding: 0 5px; color: var(--accent); }
.profile-card { display: grid; gap: 9px; padding: clamp(18px, 3vw, 30px); border: 1px solid var(--glass-border-strong); border-radius: 20px; background: var(--menu-background); color: var(--text-primary); box-shadow: 0 12px 36px var(--glass-shadow); }
.profile-card h2 { margin: 0; color: var(--heading-text); font-size: 1.12rem; }
.profile-section-caption { margin: -3px 0 8px; color: var(--text-secondary); font-size: .82rem; }
.profile-photo-row { display: grid; grid-template-columns: minmax(0, 1fr) 112px; gap: 16px; align-items: stretch; margin: 2px 0 8px; }
.profile-photo-upload { display: flex; min-height: 112px; flex-direction: column; align-items: center; justify-content: center; gap: 7px; padding: 14px; border: 1px dashed var(--control-border); border-radius: 13px; background: var(--control-background); color: var(--text-secondary); text-align: center; cursor: pointer; transition: border-color .18s ease, background .18s ease; }
.profile-photo-input { position: absolute; width: 1px; height: 1px; overflow: hidden; opacity: 0; pointer-events: none; }
.profile-photo-upload:hover { border-color: var(--accent); background: var(--interactive-hover); }
.profile-photo-upload.is-disabled { cursor: wait; opacity: .65; }
.profile-photo-upload strong { color: var(--text-primary); font-size: .84rem; }
.profile-photo-upload>span:last-child { font-size: .72rem; }
.profile-upload-icon { display: grid; width: 30px; height: 30px; place-items: center; border-radius: 9px; background: color-mix(in srgb, var(--accent) 12%, transparent); color: var(--accent); font-size: 1.25rem; font-weight: 700; }
.profile-photo-preview { display: grid; width: 112px; height: 112px; place-items: center; overflow: hidden; border: 1px solid var(--glass-border); border-radius: 14px; background: var(--control-background); }
.profile-photo-preview img { width: 100%; height: 100%; object-fit: cover; }
.profile-photo-fallback { display: grid; width: 100%; height: 100%; place-items: center; background: var(--accent); color: var(--surface); font-size: 2rem; font-weight: 800; }
.profile-fields-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 13px 16px; margin-top: 3px; }
.profile-fields-grid label { display: grid; grid-template-columns: 116px minmax(0, 1fr); align-items: center; min-width: 0; gap: 10px; color: var(--text-secondary); font-size: .78rem; font-weight: 650; }
.profile-fields-grid label > span { white-space: nowrap; }
.profile-fields-grid input,.profile-fields-grid textarea { box-sizing: border-box; width: 100%; min-height: 44px; padding: 10px 12px; border: 1px solid var(--control-border); border-radius: 10px; outline: 0; background: var(--control-background); color: var(--text-primary); font: inherit; font-size: .86rem; }
.profile-fields-grid textarea { resize: vertical; }
.profile-fields-grid input:focus,.profile-fields-grid textarea:focus { border-color: var(--accent); box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 14%, transparent); }
.profile-fields-grid input:disabled,.profile-fields-grid textarea:disabled { opacity: .62; }
.profile-address-field { grid-column: 1 / -1; }
.profile-page-actions { display: flex; align-items: center; justify-content: flex-end; gap: 14px; margin-top: 8px; padding-top: 14px; border-top: 1px solid var(--glass-border); }
.profile-upload-status { margin-inline-end: auto; color: var(--text-secondary); font-size: .8rem; }
.profile-page-actions .profile-button { min-width: 150px; }

.profile-button {
  display: inline-flex;
  min-height: 42px;
  align-items: center;
  justify-content: center;
  padding: 0 14px;
  border: 1px solid transparent;
  border-radius: 10px;
  font: inherit;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
}

.profile-button-primary {
  border-color: var(--accent-border);
  background: var(--accent);
  color: var(--surface);
}

.profile-button-ghost {
  border-color: var(--control-border);
  background: transparent;
  color: var(--text-primary);
}

.profile-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

@media (max-width: 420px) {
  .select-company-nav .select-company-brand .nav-brand {
    width: 56px;
    height: 40px;
    flex-basis: 56px;
  }

  .select-company-brand .almas-text {
    font-size: 0.88rem;
  }

  .profile-page-breadcrumb { display: none; }
}

@media (max-width: 600px) {
  .chat-list-container.is-profile-view { padding: 4px 12px 34px; }
  .profile-page-heading { align-items: flex-start; margin-top: 5px; }
  .profile-card { gap: 9px; padding: 17px 14px; border-radius: 16px; }
  .profile-photo-row { grid-template-columns: minmax(0, 1fr) 88px; gap: 10px; }
  .profile-photo-upload,.profile-photo-preview { min-height: 96px; height: 96px; }
  .profile-photo-preview { width: 88px; }
  .profile-photo-upload { padding: 9px; }
  .profile-photo-upload strong { font-size: .75rem; }
  .profile-photo-upload>span:last-child { font-size: .65rem; }
  .profile-fields-grid { grid-template-columns: 1fr; gap: 11px; }
  .profile-fields-grid label { grid-template-columns: 112px minmax(0, 1fr); gap: 8px; font-size: .73rem; }
  .profile-address-field { grid-column: auto; }
  .profile-page-actions { justify-content: stretch; }
  .profile-page-actions .profile-button { width: 100%; }
  .profile-upload-status { font-size: .72rem; }
}

@media (prefers-reduced-motion: reduce) {
  .company-language-icon {
    transition-duration: 0.01ms !important;
  }
}

/* Compact, aligned mobile navbar */
.select-company-nav :focus:not(:focus-visible) {
  outline: none;
}

.select-company-nav :focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

@media (max-width: 600px) {
  .select-company-header {
    padding: calc(env(safe-area-inset-top, 0px) + 7px) 10px 4px;
  }

  .select-company-nav {
    min-height: 52px;
    align-items: center;
    flex-direction: row;
    gap: 6px;
    padding: 5px 7px;
    border-radius: 17px;
  }

  .select-company-nav .select-company-brand {
    width: auto;
    flex: 1 1 auto;
    gap: 5px;
    overflow: hidden;
  }

  .select-company-nav .select-company-brand .nav-brand {
    width: 42px;
    height: 32px;
    flex: 0 0 42px;
  }

  .select-company-brand .almas-text {
    min-width: 0;
    max-width: 110px;
    overflow: hidden;
    font-size: clamp(0.72rem, 3.3vw, 0.82rem);
    text-overflow: ellipsis;
  }

  .select-company-nav .header-actions {
    width: auto;
    flex: 0 0 auto;
    justify-content: flex-end;
    gap: 4px;
  }

  .select-company-nav .btn-logout,
  .select-company-nav .btn-add-company,
  .select-company-nav .theme-toggle {
    width: 34px;
    min-width: 34px;
    height: 34px;
    min-height: 34px;
    flex-basis: 34px;
    padding: 5px;
    border-radius: 10px;
  }

  .select-company-nav .btn-logout svg,
  .select-company-nav .btn-add-company svg {
    width: 18px;
    height: 18px;
  }

  .select-company-nav .company-language-trigger {
    width: 92px;
    min-width: 92px;
    height: 34px;
    min-height: 34px;
    gap: 3px;
    padding: 0 5px;
    font-size: 0.68rem;
  }

  .company-language-flag {
    font-size: 0.82rem;
  }

  .company-language-icon {
    width: 13px;
    height: 13px;
    flex-basis: 13px;
  }

  .theme-glyph {
    font-size: 1.1rem;
  }
}

@media (max-width: 380px) {
  .select-company-header {
    padding-top: calc(env(safe-area-inset-top, 0px) + 6px);
  }

  .select-company-nav {
    min-height: 0;
    align-items: stretch;
    flex-direction: column;
    gap: 5px;
    padding: 6px 8px;
    border-radius: 16px;
  }

  .select-company-nav .select-company-brand {
    width: 100%;
    flex: 0 1 auto;
  }

  .select-company-nav .select-company-brand .nav-brand {
    width: 44px;
    height: 30px;
    flex-basis: 44px;
  }

  .select-company-nav .header-actions {
    width: 100%;
    justify-content: space-between;
  }

  .select-company-nav .btn-logout,
  .select-company-nav .btn-add-company,
  .select-company-nav .theme-toggle {
    width: 36px;
    min-width: 36px;
    height: 36px;
    min-height: 36px;
    flex-basis: 36px;
  }

  .select-company-nav .company-language-trigger {
    width: 92px;
    min-width: 92px;
    height: 36px;
    min-height: 36px;
  }
}

/* Reduce iOS repaint pressure while rapidly scrolling the directory */
@media (max-width: 600px) {
  .messenger-page::before,
  .messenger-page::after {
    display: none;
  }

  .app-header.select-company-header,
  .select-company-nav,
  .chat-item,
  .ai-company-card,
  .loading-state,
  .empty-state,
  .company-filter-panel,
  .company-language-menu,
  .mobile-bottom-nav,
  .modal-overlay,
  .modal-content,
  .mobile-panel-overlay,
  .profile-modal-overlay {
    -webkit-backdrop-filter: none;
    backdrop-filter: none;
  }

  .select-company-nav,
  .chat-item,
  .ai-company-card,
  .loading-state,
  .empty-state,
  .company-filter-panel,
  .company-language-menu,
  .mobile-bottom-nav,
  .modal-content {
    background: var(--surface);
  }

  .select-company-nav {
    box-shadow: 0 6px 18px var(--glass-shadow);
  }

  .chat-item,
  .ai-company-card,
  .loading-state,
  .empty-state {
    box-shadow: 0 5px 14px var(--glass-shadow);
  }

  .chat-item {
    margin: -1px  0 -1px 0;
    transition: background-color 0.15s ease, border-color 0.15s ease;
  }

  .chat-item:hover,
  .chat-item:active {
    transform: none;
    box-shadow: 0 5px 14px var(--glass-shadow);
  }

  .chat-item.selected-chat {
    box-shadow: 0 0 0 1px var(--accent-border), 0 5px 14px var(--glass-shadow);
  }

  .ai-company-card,
  .ai-company-card:hover {
    transform: none;
  }

  .mobile-bottom-nav {
    right: 0;
    left: 0;
    margin-inline: auto;
    transform: none;
    box-shadow: 0 -6px 18px var(--glass-shadow);
  }
}

/* Neutral company cards and matching theme bars */
.select-company-nav,
.mobile-bottom-nav {
  background: var(--surface);
}

.select-company-brand .almas-text {
  color: var(--focus-border);
}

.chat-list {
  gap: 8px;
}

.chat-item {
  border: 1px solid var(--glass-border);
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.chat-item:hover,
.chat-item:active {
  transform: none;
  border-color: var(--glass-border-strong);
  background: var(--surface);
  box-shadow: 0 5px 14px var(--glass-shadow);
}

.chat-item.selected-chat {
  border-color: var(--glass-border-strong);
  background: var(--surface);
  box-shadow: 0 0 0 1px var(--glass-border), 0 5px 14px var(--glass-shadow);
}

.chat-item .avatar-badge {
  border: 1px solid var(--glass-border-strong);
  background: var(--control-background-muted);
  color: var(--text-secondary);
  box-shadow: none;
}

.chat-item .mock-avatar {
  border-color: var(--glass-border-strong);
  color: var(--text-secondary);
}

.chat-item:hover .avatar {
  border-color: var(--glass-border-strong);
}

@media (max-width: 600px) {
  .select-company-nav,
  .mobile-bottom-nav {
    background: var(--surface);
  }
}

/* Desktop workspace layout: keeps the mobile composition above untouched */
.desktop-page-heading {
  display: none;
}

@media (min-width: 1024px) {
  .messenger-page {
    display: block;
    min-height: 100vh;
    padding: 0 232px 40px 0;
  }

  .app-header.select-company-header {
    position: sticky;
    z-index: 30;
    top: 0;
    padding: 18px 30px 8px;
    border: 0;
    background: transparent;
    box-shadow: none;
  }

  .select-company-nav {
    width: min(100%, 1440px);
    max-width: 1440px;
    min-height: 72px;
    margin: 0 auto;
    padding: 10px 16px;
    border-radius: 18px;
    flex-direction: row;
    align-items: center;
  }

  .select-company-nav .select-company-brand {
    flex: 0 1 auto;
    gap: 10px;
  }

  .select-company-brand .nav-brand {
    width: 64px;
    height: 46px;
    flex-basis: 64px;
  }

  .select-company-brand .almas-text {
    font-size: 1.04rem;
  }

  .select-company-nav .header-actions {
    width: auto;
    flex: 0 0 auto;
    justify-content: flex-start;
    gap: 9px;
  }

  .select-company-nav .btn-logout,
  .select-company-nav .btn-add-company,
  .select-company-nav .theme-toggle {
    width: 44px;
    min-width: 44px;
    height: 44px;
    min-height: 44px;
    flex-basis: 44px;
    margin: 0;
  }

  .select-company-nav .btn-add-company {
    display: inline-flex;
    width: auto;
    flex: 0 0 auto;
    gap: 8px;
    padding: 0 14px;
    border-color: var(--accent-border);
    background: var(--accent);
    color: var(--surface);
    font-weight: 700;
  }

  .select-company-nav .btn-add-company::after {
    content: attr(title);
    white-space: nowrap;
  }

  .select-company-nav .btn-add-company:hover {
    background: var(--accent-soft);
    color: var(--surface);
  }

  .select-company-nav .company-language-trigger {
    min-width: 112px;
    height: 44px;
    padding: 0 10px;
  }

  .desktop-page-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: min(100%, 1440px);
    margin: 22px auto 8px;
    padding: 0 30px;
  }

  .desktop-heading-copy {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .desktop-heading-copy h1 {
    margin: 0;
    color: var(--heading-text);
    font-size: clamp(1.35rem, 1.8vw, 1.8rem);
    font-weight: 800;
  }

  .desktop-heading-count {
    display: inline-grid;
    min-width: 30px;
    height: 30px;
    place-items: center;
    padding: 0 8px;
    border: 1px solid var(--glass-border);
    border-radius: 999px;
    background: var(--ghost-background);
    color: var(--text-secondary);
    font-size: 0.82rem;
    font-weight: 700;
  }

  .directory-controls {
    grid-template-columns: minmax(260px, 1fr) auto;
    width: min(100%, 1440px);
    max-width: 1440px;
    margin: 12px auto 18px;
    padding: 0 30px;
    gap: 12px;
  }

  .directory-search,
  .filter-toggle {
    min-height: 48px;
  }

  .company-filter-panel {
    grid-template-columns: repeat(4, minmax(0, 1fr)) auto;
    align-items: end;
    width: min(calc(100% - 60px), 1380px);
    max-width: 1380px;
    margin: 0 auto 20px;
    padding: 18px;
    border-radius: 16px;
    background: var(--surface-strong);
    box-shadow: 0 10px 26px var(--glass-shadow);
    -webkit-backdrop-filter: none;
    backdrop-filter: none;
  }

  .filter-panel-heading {
    grid-column: 1 / -1;
  }

  .clear-filters {
    grid-column: auto;
    min-height: 42px;
    padding: 0 14px;
    white-space: nowrap;
  }

  .chat-list-container {
    width: min(100%, 1440px);
    max-width: 1440px;
    padding: 4px 30px 48px;
  }

  .chat-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 340px), 1fr));
    align-items: stretch;
    gap: 18px;
  }

  .chat-item {
    display: grid;
    grid-template-columns: 72px minmax(0, 1fr);
    align-items: start;
    gap: 16px;
    min-height: 136px;
    padding: 16px 18px;
    border-radius: 18px;
    background: var(--surface);
    box-shadow: 0 8px 22px var(--glass-shadow);
  }

  .chat-item:hover,
  .chat-item:active {
    transform: translateY(-2px);
    border-color: var(--glass-border-strong);
    background: var(--surface);
    box-shadow: 0 12px 28px var(--glass-shadow);
  }

  .chat-item.selected-chat {
    border-color: var(--glass-border-strong);
    background: var(--surface);
    box-shadow: 0 0 0 1px var(--glass-border), 0 10px 24px var(--glass-shadow);
  }

  .avatar-container {
    grid-column: 1;
    grid-row: 1;
    width: 72px;
    margin: 0;
  }

  .avatar {
    width: 72px;
    height: 72px;
  }

  .chat-content {
    grid-column: 2;
    grid-row: 1;
    display: flex;
    flex-direction: column;
    gap: 14px;
    min-width: 0;
  }

  .chat-top-row,
  .chat-title-wrapper {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
  }

  .chat-title {
    font-size: 1.05rem;
    line-height: 1.5;
    white-space: normal;
    text-align: start;
  }

  .chat-bottom-row {
    margin-top: 2px;
  }

  .chat-subtitle {
    display: -webkit-box;
    overflow: hidden;
    white-space: normal;
    line-height: 1.65;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 4;
    text-align: right;
  }

  .mobile-bottom-nav {
    position: fixed;
    z-index: 80;
    top: 0;
    right: 0;
    bottom: 0;
    left: auto;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 220px;
    height: 100vh;
    height: 100dvh;
    padding: 24px 14px;
    gap: 7px;
    transform: none;
    border: 0;
    border-left: 1px solid var(--glass-border-strong);
    border-radius: 0;
    background: var(--surface);
    box-shadow: -10px 0 28px var(--glass-shadow);
    -webkit-backdrop-filter: none;
    backdrop-filter: none;
  }

  .bottom-nav-item {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    min-height: 48px;
    padding: 0 12px;
    border-radius: 12px;
    font-size: 0.88rem;
    text-align: start;
  }

  .bottom-nav-item svg {
    width: 21px;
    height: 21px;
    flex: 0 0 21px;
  }

  .bottom-nav-item span {
    flex: 1;
    text-align: start;
  }

  .bottom-nav-item:nth-of-type(3) { order: 1; }
  .bottom-nav-item:nth-of-type(4) { order: 2; }
  .bottom-nav-item:nth-of-type(2) { order: 3; }
  .bottom-nav-item:nth-of-type(1) {
    order: 4;
    margin-top: auto;
  }

  .bottom-nav-count {
    position: static;
    right: auto;
    top: auto;
    margin-inline-start: auto;
  }

  .ai-companies-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 340px), 1fr));
    width: min(100%, 1440px);
    max-width: 1440px;
    margin: 0 auto;
    padding: 4px 30px 48px;
    gap: 18px;
  }

  .ai-company-card {
    min-height: 150px;
  }

  .loading-state,
  .empty-state {
    width: min(calc(100% - 60px), 700px);
    margin: 24px auto;
  }
}
.chat-subtitle {
  color: var(--text-primary);
  text-align: right;
}

.chat-list .chat-item {
  background: var(--card-background);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 var(--glass-shadow), 0 1px 2px -1px var(--glass-shadow);
}

.chat-list .chat-item:hover,
.chat-list .chat-item:active {
  background: var(--surface-hover);
  border-color: var(--glass-border-strong);
}

.chat-list .chat-item.selected-chat {
  background: var(--surface-strong);
  border-color: var(--accent-border);
  box-shadow: 0 0 0 1px var(--accent-border), 0 6px 18px var(--glass-shadow);
}

@media (max-width: 1023px) {
  .chat-list {
    gap: 8px;
  }
}


.chat-list .chat-item .mock-avatar {
  background: hsl(var(--company-avatar-hue, 220) var(--company-avatar-fill-tone));
  border-color: hsl(var(--company-avatar-hue, 220) var(--company-avatar-outline-tone));
  color: hsl(var(--company-avatar-hue, 220) var(--company-avatar-letter-tone));
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1;
}

/* Group the shared header controls and give each control the same hit area. */
.select-company-nav .header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.select-company-nav .header-utility-controls {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px;
  border: 1px solid var(--glass-border);
  border-radius: 15px;
  background: var(--control-background-muted);
}

.select-company-nav .header-utility-controls .btn-logout,
.select-company-nav .header-utility-controls .theme-toggle,
.select-company-nav .header-utility-controls .company-language-trigger {
  box-sizing: border-box;
  width: 42px;
  min-width: 42px;
  height: 42px;
  min-height: 42px;
  flex: 0 0 42px;
  margin: 0;
  padding: 0;
  border-radius: 11px;
}

.select-company-nav .header-utility-controls .company-language-picker {
  position: relative;
  flex: 0 0 42px;
}

.select-company-nav .header-utility-controls .company-language-trigger {
  justify-content: center;
  gap: 3px;
  border: 1px solid var(--control-border);
  background: var(--control-background);
  color: var(--text-primary);
  font: inherit;
}

.company-language-code {
  font-size: 0.65rem;
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0.02em;
}

.select-company-nav .header-utility-controls .company-language-chevron {
  width: 12px;
  height: 12px;
  flex: 0 0 12px;
  color: var(--text-muted);
}

.select-company-nav .header-utility-controls .theme-toggle {
  color: var(--accent);
}

@media (max-width: 600px) {
  .select-company-nav .header-utility-controls {
    gap: 4px;
    padding: 3px;
  }

  .select-company-nav .header-utility-controls .btn-logout,
  .select-company-nav .header-utility-controls .theme-toggle,
  .select-company-nav .header-utility-controls .company-language-trigger {
    width: 38px;
    min-width: 38px;
    height: 38px;
    min-height: 38px;
    flex-basis: 38px;
  }

  .select-company-nav .header-utility-controls .company-language-picker {
    flex-basis: 38px;
  }

  .select-company-nav .header-utility-controls .company-language-chevron {
    width: 10px;
    height: 10px;
    flex-basis: 10px;
  }
}

/* Status badges sit in the card heading, separate from the company avatar. */
.chat-list .chat-item .chat-top-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  flex-wrap: wrap;
}

.chat-list .chat-item .chat-title-wrapper {
  flex: 1 1 10rem;
  min-width: 0;
}

.chat-list .chat-item .company-type-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  min-height: 24px;
  padding: 3px 9px;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1.25;
  white-space: nowrap;
}

.chat-list .chat-item .company-type-badge--public {
  background: var(--company-badge-public-background);
  border-color: var(--company-badge-public-border);
  color: var(--company-badge-public-text);
}

.chat-list .chat-item .company-type-badge--store {
  background: var(--company-badge-store-background);
  border-color: var(--company-badge-store-border);
  color: var(--company-badge-store-text);
}

.chat-list .chat-item .company-type-badge--private {
  background: var(--company-badge-private-background);
  border-color: var(--company-badge-private-border);
  color: var(--company-badge-private-text);
}

.select-company-nav .btn-add-company {
  background: var(--company-create-background);
  border-color: var(--company-create-background);
  color: var(--company-create-text);
}

.select-company-nav .btn-add-company:hover,
.select-company-nav .btn-add-company:focus-visible {
  background: var(--company-create-hover-background);
  border-color: var(--company-create-hover-background);
  color: var(--company-create-text);
}

.desktop-heading-actions { display: flex; align-items: center; justify-content: space-between; width: 100%; gap: 16px; }
.messenger-page[dir="ltr"] .desktop-heading-actions { flex-direction: row-reverse; }
.desktop-add-company-button { display: none; align-items: center; justify-content: center; gap: 8px; min-height: 42px; padding: 0 14px; border: 1px solid var(--company-create-background); border-radius: 12px; background: var(--company-create-background); color: var(--company-create-text); font: inherit; font-weight: 700; cursor: pointer; }
.desktop-add-company-button:hover, .desktop-add-company-button:focus-visible { border-color: var(--company-create-hover-background); background: var(--company-create-hover-background); outline: 2px solid var(--accent-border); outline-offset: 2px; }
.desktop-add-company-button svg { width: 19px; height: 19px; }
.mobile-add-company-fab { display: none; }
.desktop-page-heading {
  display: none;
  align-items: center;
  justify-content: space-between;
  width: min(calc(100% - 24px), 560px);
  margin: 14px auto 6px;
  padding: 0 16px;
}
.desktop-heading-copy { display: flex; align-items: center; gap: 10px; }
.desktop-heading-copy h1 { margin: 0; color: var(--heading-text); font-size: 1.15rem; font-weight: 800; }
.desktop-heading-count { display: inline-grid; min-width: 27px; height: 27px; place-items: center; padding: 0 7px; border: 1px solid var(--glass-border); border-radius: 999px; background: var(--ghost-background); color: var(--text-secondary); font-size: .76rem; font-weight: 700; }

.company-showcase { display: grid; gap: 15px; width: min(calc(100% - 24px), 560px); margin: 12px auto 14px; }
.showcase-group { display: grid; min-width: 0; gap: 8px; }
.showcase-section-heading { position: relative; display: flex; min-height: 34px; align-items: center; justify-content: flex-start; gap: 12px; text-align: right; }
.showcase-section-heading h2 { margin: 0; padding-inline: 40px; color: var(--heading-text); font-size: 1rem; font-weight: 800; text-align: right; }
.showcase-carousel-controls { position: absolute; z-index: 2; inset: 0; display: none; align-items: center; justify-content: space-between; pointer-events: none; }
.showcase-carousel-controls.has-overflow { display: flex; }
.showcase-carousel-nav { display: grid; flex: 0 0 34px; width: 34px; height: 34px; place-items: center; padding: 0; border: 1px solid var(--glass-border); border-radius: 11px; background: var(--surface); color: var(--text-secondary); cursor: pointer; transition: border-color .18s ease, color .18s ease, background .18s ease; }
.showcase-carousel-nav { pointer-events: auto; }
.showcase-carousel-nav:hover, .showcase-carousel-nav:focus-visible { border-color: var(--accent-border); background: var(--surface-hover); color: var(--accent); outline: 2px solid var(--accent-border); outline-offset: 2px; }
.showcase-carousel-nav svg { width: 18px; height: 18px; }
.messenger-page[dir="rtl"] .showcase-carousel-nav svg { transform: rotate(180deg); }
.showcase-carousel-shell { position: relative; min-width: 0; width: 100%; }
.showcase-carousel-track { --showcase-card-size: clamp(82px, 27vw, 104px); --showcase-fade-left: 0px; --showcase-fade-right: 0px; display: flex; min-width: 0; gap: 8px; overflow-x: auto; overflow-y: hidden; padding: 2px 8px 8px; overscroll-behavior-x: contain; scroll-behavior: smooth; scroll-padding-inline: 8px; scroll-snap-type: x mandatory; scrollbar-width: none; -webkit-overflow-scrolling: touch; -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 var(--showcase-fade-left), #000 calc(100% - var(--showcase-fade-right)), transparent 100%); mask-image: linear-gradient(90deg, transparent 0, #000 var(--showcase-fade-left), #000 calc(100% - var(--showcase-fade-right)), transparent 100%); }
.showcase-carousel-shell.has-more-left .showcase-carousel-track { --showcase-fade-left: 28px; }
.showcase-carousel-shell.has-more-right .showcase-carousel-track { --showcase-fade-right: 28px; }
.showcase-carousel-track::-webkit-scrollbar { display: none; }
.showcase-quick-links { gap: 10px; }
.showcase-quick-card { display: flex; flex: 0 0 var(--showcase-card-size); align-items: center; justify-content: center; flex-direction: column; gap: 6px; box-sizing: border-box; width: var(--showcase-card-size); height: var(--showcase-card-size); min-width: 0; min-height: 0; padding: 7px; overflow: hidden; scroll-snap-align: start; scroll-snap-stop: always; border: 1px solid var(--glass-border); border-radius: 15px; background: var(--card-background); color: var(--text-primary); text-align: center; text-decoration: none; box-shadow: 0 5px 16px var(--glass-shadow); }
.showcase-quick-card:hover, .showcase-quick-card:focus-visible { border-color: var(--accent-border); background: var(--surface-hover); outline: none; box-shadow: 0 0 0 3px var(--chart-glow); }
.showcase-quick-icon { display: grid; flex: 0 0 28px; width: 28px; height: 28px; place-items: center; border: 1px solid var(--accent-border); border-radius: 50%; background: var(--ghost-background); color: var(--accent); }
.showcase-quick-icon svg { width: 17px; height: 17px; }
.showcase-quick-copy { display: grid; justify-items: center; min-width: 0; gap: 4px; text-align: center; }
.showcase-quick-copy strong { color: var(--heading-text); font-size: .72rem; line-height: 1.35; }
.showcase-section-heading h2 { line-height: 1.4; }
.showcase-card-grid { gap: 10px; }
.showcase-company-card { display: flex; flex: 0 0 var(--showcase-card-size); align-items: center; justify-content: center; flex-direction: column; gap: 4px; box-sizing: border-box; width: var(--showcase-card-size); height: var(--showcase-card-size); min-width: 0; min-height: 0; padding: 7px; overflow: hidden; scroll-snap-align: start; scroll-snap-stop: always; border: 1px solid var(--glass-border); border-radius: 15px; background: var(--card-background); color: var(--text-primary); font: inherit; text-align: center; text-decoration: none; box-shadow: 0 6px 18px var(--glass-shadow); }
.showcase-company-card:hover { border-color: var(--glass-border-strong); background: var(--surface-hover); }
.showcase-company-card:focus-visible { border-color: var(--accent-border); outline: 2px solid var(--accent-border); outline-offset: 2px; box-shadow: 0 0 0 3px var(--chart-glow), 0 6px 18px var(--glass-shadow); }
.showcase-company-card.is-selected { border-color: var(--accent-border); box-shadow: 0 0 0 1px var(--accent-border), 0 6px 18px var(--glass-shadow); }
.showcase-company-card-top { display: flex; align-items: center; justify-content: center; width: 100%; gap: 8px; }
.showcase-company-avatar { display: grid; flex: 0 0 34px; width: 34px; height: 34px; place-items: center; overflow: hidden; border: 1px solid var(--glass-border-strong); border-radius: 50%; background: var(--ghost-background); color: var(--accent); font-size: .9rem; font-weight: 800; }
.showcase-company-avatar img { width: 100%; height: 100%; object-fit: cover; }
.showcase-company-card h3 { margin: 1px 0 0; color: var(--heading-text); font-size: .68rem; font-weight: 800; line-height: 1.3; text-align: center; }
.showcase-company-card p { display: -webkit-box; min-height: 2.6em; margin: 0; overflow: hidden; color: var(--text-secondary); font-size: .56rem; line-height: 1.3; text-align: center; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.showcase-empty-card { align-items: center; justify-content: center; gap: 5px; border-style: dashed; background: var(--card-background); text-align: center; }
.showcase-empty-icon { display: grid; width: 30px; height: 30px; place-items: center; border: 1px solid var(--glass-border); border-radius: 50%; color: var(--text-muted); font-size: 1.1rem; }
.showcase-empty-card strong { color: var(--text-secondary); font-size: .58rem; line-height: 1.3; }
.showcase-empty-card > span:last-child { color: var(--text-muted); font-size: .54rem; }
@media (max-width: 370px) {
  .company-showcase { width: calc(100% - 20px); gap: 13px; }
  .showcase-carousel-track { --showcase-card-size: clamp(78px, 26vw, 96px); padding-inline: 6px; scroll-padding-inline: 6px; }
  .showcase-quick-card { gap: 5px; padding: 6px; }
  .showcase-quick-icon { flex-basis: 26px; width: 26px; height: 26px; }
  .showcase-quick-icon svg { width: 16px; height: 16px; }
  .showcase-quick-copy strong { font-size: .66rem; }
  .showcase-company-avatar { flex-basis: 32px; width: 32px; height: 32px; }
}
@media (min-width: 1024px) {
  .desktop-page-heading { display: flex; width: min(100%, 1440px); max-width: 1440px; margin: 17px auto 8px; padding: 0 30px; }
  .desktop-add-company-button { display: inline-flex; }
  .mobile-add-company-fab { display: none !important; }
  .desktop-heading-copy h1 { font-size: clamp(1.35rem, 1.8vw, 1.8rem); }
  .company-showcase { width: min(100%, 1440px); max-width: 1440px; margin: 10px auto 14px; padding: 0 30px; gap: 18px; }
  .showcase-group { gap: 9px; }
  .showcase-section-heading h2 { font-size: 1.08rem; }
  .showcase-carousel-controls.has-overflow { display: flex; }
  .showcase-carousel-track { --showcase-card-size: clamp(164px, 14vw, 176px); gap: 14px; padding: 2px 16px 10px; scroll-padding-inline: 16px; }
  .showcase-quick-card { padding: 14px; border-radius: 18px; }
  .showcase-quick-icon { flex-basis: 38px; width: 38px; height: 38px; }
  .showcase-quick-copy strong { font-size: .9rem; }
  .showcase-company-avatar { flex-basis: 46px; width: 46px; height: 46px; }
  .showcase-company-card { padding: 14px; border-radius: 18px; }
  .showcase-company-card h3 { font-size: .92rem; }
  .showcase-company-card p { font-size: .72rem; }
}
@media (max-width: 420px) {
  .select-company-header { padding-inline: 6px; }
  .select-company-nav { flex-direction: row; flex-wrap: nowrap; align-items: center; gap: 4px; min-height: 54px; padding: 5px 6px; border-radius: 15px; }
  .select-company-nav .select-company-brand { width: auto; min-width: max-content; flex: 0 0 auto; gap: 3px; overflow: visible; }
  .select-company-brand .nav-brand { width: 36px; height: 30px; flex: 0 0 36px; }
  .select-company-brand .almas-text { max-width: none; flex: 0 0 auto; font-size: .72rem; white-space: nowrap; overflow: visible; text-overflow: clip; }
  .select-company-nav .header-actions { width: auto; min-width: 0; flex: 0 0 auto; gap: 4px; }
  .select-company-nav .header-utility-controls { gap: 2px; padding: 2px; border-radius: 11px; }
  .select-company-nav .header-utility-controls .btn-logout,
  .select-company-nav .header-utility-controls .theme-toggle,
  .select-company-nav .header-utility-controls .company-language-trigger { width: 30px; min-width: 30px; height: 30px; min-height: 30px; flex-basis: 30px; border-radius: 8px; }
  .select-company-nav .header-utility-controls .company-language-picker { flex-basis: 30px; }
  .select-company-nav .header-utility-controls .company-language-chevron { width: 8px; height: 8px; flex-basis: 8px; }
  .select-company-nav .header-utility-controls .btn-logout svg { width: 16px; height: 16px; }
  .select-company-nav .btn-add-company { width: 32px; min-width: 32px; height: 32px; min-height: 32px; flex: 0 0 32px; padding: 5px; border-radius: 9px; }
  .select-company-nav .btn-add-company svg { width: 18px; height: 18px; }
}
@media (max-width: 1023px) {
  .desktop-page-heading { display: flex; }
  .desktop-heading-actions { flex-wrap: nowrap; gap: 8px; }
  .desktop-heading-copy { flex: 0 1 auto; min-width: 0; white-space: nowrap; }
  .desktop-heading-copy h1 { white-space: nowrap; }
  .desktop-add-company-button { display: inline-flex; flex: 0 0 auto; min-height: 36px; gap: 6px; padding: 0 10px; font-size: .76rem; white-space: nowrap; }
  .desktop-add-company-button svg { width: 17px; height: 17px; }
  .mobile-add-company-fab { position: fixed; z-index: 81; inset-inline-end: 18px; bottom: calc(88px + env(safe-area-inset-bottom, 0px)); display: grid; width: 54px; height: 54px; place-items: center; padding: 0; border: 1px solid var(--company-create-background); border-radius: 50%; background: var(--company-create-background); color: var(--company-create-text); box-shadow: 0 8px 24px var(--glass-shadow); cursor: pointer; animation: company-fab-enter .2s ease-out both; }
  .mobile-add-company-fab:hover, .mobile-add-company-fab:focus-visible { border-color: var(--company-create-hover-background); background: var(--company-create-hover-background); outline: 2px solid var(--accent-border); outline-offset: 3px; }
  .mobile-add-company-fab svg { width: 24px; height: 24px; }
}
@media (max-width: 370px) {
  .desktop-page-heading { padding-inline: 10px; }
  .desktop-heading-actions { gap: 6px; }
  .desktop-heading-copy { gap: 6px; }
  .desktop-add-company-button { min-height: 34px; gap: 4px; padding-inline: 7px; font-size: .68rem; }
  .desktop-add-company-button svg { width: 15px; height: 15px; }
}
@keyframes company-fab-enter { from { opacity: 0; transform: translateY(8px) scale(.92); } to { opacity: 1; transform: translateY(0) scale(1); } }
.showcase-company-card {
  cursor: pointer;
  transition: transform 0.18s, box-shadow 0.18s;
}
.showcase-company-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.12);
}
.showcase-company-card:focus-visible {
  outline: 2px solid var(--accent, var(--primary));
  outline-offset: 2px;
}
.showcase-company-card {
  position: relative;
  cursor: pointer;
}

.showcase-company-card.is-loading {
  pointer-events: none;
  opacity: 0.85;
}

.card-loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  border-radius: inherit;
  z-index: 5;
  backdrop-filter: blur(2px);
}

.card-loading-overlay .spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: card-spin 0.8s linear infinite;
}

@keyframes card-spin {
  to { transform: rotate(360deg); }
}
.showcase-company-card {
  position: relative;
}

.card-loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  border-radius: inherit;
  z-index: 5;
  pointer-events: none;
}

.card-loading-overlay .spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: card-spin 0.8s linear infinite;
}

@keyframes card-spin {
  to { transform: rotate(360deg); }
}
/* ========================================================
   بزرگ‌تر کردن اندازه آواتار شرکت‌ها و فروشگاه‌های برگزیده
   ======================================================== */
.showcase-company-avatar {
  /* افزایش ابعاد آواتار */
  width: 45px !important;
  height: 45px !important;
  min-width: 45px !important;
  min-height: 45px !important;
  
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: var(--surface-hover, #f1f5f9);
  border: 2px solid var(--border-color, #e2e8f0);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

/* تنظیم اندازه تصویر لوگو داخل آواتار */
.showcase-company-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* تنظیم متن اختصاری/حرف اول شرکت در صورت نبود لوگو */
.showcase-company-avatar span {
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--primary, #4f46e5);
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
}

/* ایجاد فضای مناسب در بخش بالایی کارت */
.showcase-company-card-top {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 12px;
}

/* افکت جزئی بزرگ‌نمایی هنگام هاور روی کارت */
.showcase-company-card:hover .showcase-company-avatar {
  transform: scale(1.06);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
}

.parent-selection-overlay {
  position: fixed;
  z-index: 20000;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.58);
  backdrop-filter: blur(4px);
}

.parent-selection-dialog {
  width: min(100%, 520px);
  max-height: min(82vh, 700px);
  overflow-y: auto;
  padding: 26px;
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 18px;
  background: var(--bg, #fff);
  color: var(--text, #172033);
  text-align: start;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.25);
}

.parent-selection-dialog h2 {
  margin: 0 0 8px;
  font-size: 1.25rem;
}

.parent-selection-dialog > p {
  margin: 0 0 18px;
  color: var(--text-muted, #64748b);
  line-height: 1.8;
}

.parent-selection-list {
  display: grid;
  gap: 10px;
  max-height: 42vh;
  overflow-y: auto;
}

.parent-selection-option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  border: 1px solid var(--border, #dbe2ea);
  border-radius: 12px;
  background: var(--card-bg, transparent);
  cursor: pointer;
  transition: border-color .16s ease, background .16s ease;
}

.parent-selection-option.is-selected {
  border-color: var(--primary, #4f46e5);
  background: color-mix(in srgb, var(--primary, #4f46e5) 8%, transparent);
}

.parent-selection-option input {
  margin-top: 4px;
  accent-color: var(--primary, #4f46e5);
}

.parent-selection-option-copy {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.parent-selection-option-copy span {
  color: var(--text-muted, #64748b);
  font-size: .9rem;
  overflow-wrap: anywhere;
}

.parent-selection-error {
  margin: 12px 0 0 !important;
  color: #b42318 !important;
}

.parent-selection-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 22px;
}

.parent-selection-actions button {
  min-width: 105px;
  padding: 10px 16px;
  border: 0;
  border-radius: 10px;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.parent-selection-cancel {
  background: var(--surface, #eef2f7);
  color: var(--text, #172033);
}

.parent-selection-confirm {
  background: var(--primary, #4f46e5);
  color: white;
}

.parent-selection-confirm:disabled {
  opacity: .55;
  cursor: not-allowed;
}

@media (max-width: 520px) {
  .parent-selection-dialog { padding: 20px; }
  .parent-selection-actions { flex-direction: column-reverse; }
  .parent-selection-actions button { width: 100%; }
}

</style>
